import axios, { type AxiosInstance, type AxiosError } from "axios";
import { getSessionToken } from "@/lib/session";
import type {
  Account,
  AccountDetail,
  BalanceHistoryPoint,
  ClientProfile,
  DashboardSummary,
  PaginatedResponse,
  Statement,
  StatementRequest,
  Trade,
  TradeDetail,
  Transaction,
  TransactionDetail,
  TradeFilters,
  TransactionFilters,
  ApiError,
} from "@/types";

// ── Team Performance: trader period comparison ──────────────────

export interface NewClientRow {
  accountId: string;
  accountName: string;
  vertical: string | null;
  status: string | null;
  /** Account.CreatedDate — can be months before the first trade. */
  createdDate: string | null;
  /** What the count is keyed on. */
  firstTradeDate: string;
  firstTradeMonth: string;
  /** Lifetime revenue since landing, not just the first trade. */
  revenueSince: number;
}

export interface PeriodMonthMargin {
  vwaMarginPct: number | null;
  volume: number;
  profit: number;
}

export interface PeriodBlock {
  start: string;
  end: string;
  months: string[];
  activeClients: { count: number; byMonth: Record<string, number> };
  revenue: { total: number; byMonth: Record<string, number> };
  margin: {
    vwaMarginPct: number | null;
    volume: number;
    profit: number;
    tradeCount: number;
    byMonth: Record<string, PeriodMonthMargin>;
  };
  newClients: { count: number; byMonth: Record<string, number>; accounts: NewClientRow[] };
}

export interface PeriodComparison {
  traderId: string;
  traderName: string;
  periodKind: "quarter" | "half";
  periodIndex: number;
  year: number;
  excludePayroll: boolean;
  currentLabel: string;
  priorLabel: string;
  current: PeriodBlock;
  prior: PeriodBlock;
  bookAccountCount: number;
  ownedAccountCount: number;
  ownerFieldAvailable: boolean;
  newClientsLast12Months: Array<{ month: string; count: number; accounts: NewClientRow[] }>;
  deltas: {
    activeClients: number | null;
    revenue: number | null;
    margin: number | null;
    newClients: number | null;
  };
}

// ── iFAST Interest Earning ──────────────────────────────────────
// Shapes returned by /admin/kpis/interest*. Declared here rather than in
// @/types because nothing outside the KPI tab consumes them.

export interface InterestTier {
  floor: number;
  ratePct: number;
}

export interface InterestTierRow extends InterestTier {
  tier: number;
  ceiling: number | null;
  amountInBand: number;
  annualInterest: number;
}

export interface InterestLivePot {
  currency: string;
  pot: "viban" | "pooled";
  balance: number;
  accountCount: number;
  tiers: InterestTierRow[];
  dailyInterest: number;
}

export interface InterestActual {
  id: string;
  month: string;
  currency: string;
  amountCcy: number;
  amountGbp: number;
  notes: string | null;
  updatedAt: string | null;
}

export interface InterestPotSummary {
  interest: number;
  days: number;
  avgBalance: number;
  peakBalance: number;
}

export interface InterestCurrency {
  currency: string;
  pots: Record<string, InterestPotSummary>;
  estimateCcy: number;
  estimateGbp: number;
  actual: InterestActual | null;
  usedGbp: number;
  basis: "estimate" | "actual";
}

export interface InterestSnapshot {
  date: string;
  currency: string;
  pot: "viban" | "pooled";
  balance: number;
  accountCount: number;
  interest: number;
  source: string;
  capturedAt: string | null;
}

export interface InterestMonth {
  month: string;
  currencies: InterestCurrency[];
  totalEstimateGbp: number;
  totalRevenueGbp: number;
  isFullyActual: boolean;
  daysCaptured: number;
  daysElapsed: number;
  daysInMonth: number;
  fxRates: { source: string; updatedAt: string | null; disclaimer: string };
  snapshots: InterestSnapshot[];
  missingDays: string[];
  rateCard: Record<string, InterestTier[]>;
  cutoffHours: Record<string, number>;
  cutoffTimezone: string;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${BASE_URL}/api/v1`,
    timeout: 15_000,
    headers: { "Content-Type": "application/json" },
  });

  // Attach auth token to every request
  client.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Impersonation: if a session is active client-side, stamp the
    // headers on every outgoing request. Direct localStorage read
    // avoids a circular import (store → api → store).
    try {
      const raw = localStorage.getItem("millbank-impersonation");
      if (raw) {
        const parsed = JSON.parse(raw);
        const s = parsed?.state;
        if (s?.target?.id && s?.startedAt) {
          const ageMs = Date.now() - new Date(s.startedAt).getTime();
          const maxMs = (s.maxDurationSeconds || 3600) * 1000;
          if (ageMs < maxMs) {
            config.headers["X-Impersonate-User-Id"] = s.target.id;
            config.headers["X-Impersonate-Started-At"] = s.startedAt;
          }
        }
      }
    } catch {
      // localStorage unavailable / parse failure — fall through unimpersonated.
    }
    return config;
  });

  // Retry once on 5xx
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError<ApiError>) => {
      const config = error.config as typeof error.config & { _retry?: boolean };
      if (error.response && error.response.status >= 500 && !config?._retry) {
        (config as any)._retry = true;
        await new Promise((r) => setTimeout(r, 1000));
        return client(config!);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const client = createClient();

export interface ClientDocument {
  id: string;
  accountId: string;
  accountName: string | null;
  docType: string;
  filename: string;
  storagePath?: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedByEmail: string | null;
  createdAt: string | null;
}

// Shape of one row from an uploaded corrections CSV. Mirrors the
// keys the backend's parse_corrections_csv produces.
export interface CorrectionRow {
  payeeName: string;
  sourceFile: string;
  originalAccountNumber: string;
  correctedAccountNumber: string;
  correctedSortCode: string;
  correctedBankName: string;
}

// Orphan payment row — detached from its source file, awaiting edit /
// SF push / inclusion in a fresh bank file.
export interface CutoffConfig {
  timezone: string;
  banks: Array<{
    bankId: string; bankName: string; enabled: boolean; stale: boolean;
    currencies: Array<{
      currency: string; accounts: number; stale: boolean;
      // label "" is the plain currency; others are rails/routes (FPS, Banking Circle)
      variants: Array<{ label: string; cutoff: string | null; alwaysOpen: boolean }>;
    }>;
  }>;
}
export interface CutoffToday {
  asOf: string; timezone: string; weekend: boolean;
  banks: Array<{
    bankId: string; bankName: string; nextCutoffAt: string | null;
    currencies: Array<{ currency: string; label: string; alwaysOpen: boolean; cutoff: string | null; cutoffAt: string | null; secondsRemaining: number | null; passed: boolean }>;
  }>;
}

// ── Leads KPIs (KPIs → Leads tab) ───────────────────────────────

export type LeadKpiPeriod = "30d" | "90d" | "12m" | "all";

export interface LeadKpiFunnelStep {
  stage: string;
  reached: number;
  pctOfPrevious: number | null;
}

export interface LeadKpiRow {
  leads: number;
  processed: number;
  opened: number;
  conversionRate: number | null;
  funnel: LeadKpiFunnelStep[];
}

export interface LeadKpiPm extends LeadKpiRow {
  pmId: string;
  pmName: string;
  avgFirstTouchHours: number | null;
  firstTouchSample: number;
  stageHours: Record<string, { avgHours: number | null; count: number }>;
}

export interface LeadKpiSource extends LeadKpiRow {
  source: string;
  firstImported: string | null;
}

export interface LeadKpis {
  period: LeadKpiPeriod;
  durationStages: string[];
  funnelStages: string[];
  team: Omit<LeadKpiPm, "pmId" | "pmName">;
  pms: LeadKpiPm[];
  sources: LeadKpiSource[];
}

// ── Bank Payments (Freemarket recon) ────────────────────────────

export interface BpPrep {
  amount: number | null;
  currency: string;
  currencyId: string;
  accountId: string;
  accountName: string;
  nostroAccountId: string;
  nostroAccountName: string;
  creditInstitutionId: string;
  creditInstitutionName: string;
  hasThirdParty: boolean;
  thirdPartyId: string;
  thirdPartyName: string;
  invoiceNumber: string;
  skipInvoice: boolean;
  /** How the credit institution / third party were derived, for the reviewer. */
  derivationNote: string;
  /** Suggested invoice reference. Put in invoiceNumber for the reviewer to
   *  check; what they push against it is recorded and trains the model. */
  invoiceSuggestion?: {
    value: string; source: string; confidence: string; reason: string;
  };
  /** The suggestion was put in the invoice field (once; never refilled). */
  invoicePrefilled?: boolean;
  valueDate: string;
  status: string;
  amountGbp: number | null;
  fxRate: number | null;
  fxRateSource: string;
  fxRateError: string;
}

/** Proposed invoice reference against what was pushed. Shares are of
 *  payments where a reference was proposed; coverage is of all pushed. */
export interface BpSuggestionSplit {
  total: number; offered: number;
  counts: { as_proposed: number; edited: number; different: number; no_invoice: number; no_suggestion: number };
  coverage: number | null; asProposed: number | null; edited: number | null; different: number | null; noInvoice: number | null;
}
export interface BpSuggestionReport {
  overall: BpSuggestionSplit;
  byEra: { offered?: BpSuggestionSplit; prefilled?: BpSuggestionSplit };
  byBank: Array<BpSuggestionSplit & { bank: string }>;
  bySource: Array<BpSuggestionSplit & { source: string }>;
  byMonth: Array<BpSuggestionSplit & { month: string }>;
  recentMisses: Array<{
    when: string | null; outcome: "edited" | "different" | "no_invoice";
    proposed: string; used: string; payer: string; bank: string; source: string | null; narrative: string[];
  }>;
  from: string | null; to: string | null;
}

export interface BpQueueItem {
  id: string;
  fmEnv: string;
  fmAccountId: string;
  fmTransactionId: string;
  fmKind: string | null;
  fmAmount: number | null;
  fmCurrency: string | null;
  fmValueDate: string | null;
  fmCreatedDate: string | null;
  fmCounterpartyName: string | null;
  fmCounterpartyIban: string | null;
  fmCounterpartyAccount: string | null;
  fmCounterpartyBank: string | null;
  fmStatus: string | null;
  fmReference: string | null;
  fmNarrative: string[];
  fmRaw: Record<string, any>;
  prep: Partial<BpPrep>;
  status: "pending" | "compliance" | "pushed" | "ignored" | "matched_in_sf";
  complianceNote: string | null;
  complianceSentAt: string | null;
  complianceClearedAt: string | null;
  sfBankPaymentId: string | null;
  sfInvoiceId: string | null;
  sfTransactionId: string | null;
  pushError: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  pushedAt: string | null;
}

export interface BpBalance {
  fmEnv: string;
  fmAccountId: string;
  fmAccountName: string | null;
  currency: string | null;
  iban: string | null;
  accountNumber: string | null;
  fmBalance: number | null;
  fmAvailable: number | null;
  sfBalance: number | null;
  difference: number | null;
  hasDiscrepancy: boolean;
  sfBankAccountId: string | null;
  sfBankAccountName: string | null;
  sfAccountId: string | null;
  sfAccountName: string | null;
  matchState: string;
  candidatesFound: number;
  queuedCount: number;
  note: string | null;
  lastScannedAt: string | null;
}

export type BpTab = "queue" | "balances" | "exclusions" | "counterparties" | "audit" | "ifast_statements" | "ifast_recon" | "balance_settings";

/** An account left out of balances, reports and grouped data (Balance settings). */
export interface ExcludedAccount { accountId: string; accountName: string; reason: string; addedBy: string; addedAt: string | null }

export interface BpOverview {
  /** The tabs this user can open (one permission each). */
  tabs: BpTab[];
  /** Holds the review queue permission, so can run a scan. */
  canScan: boolean;
  /** Empty without the Balances tab. */
  balances: BpBalance[];
  /** Queue counts only with the Review queue tab; discrepancies/accounts only with Balances. */
  counts: {
    pending?: number; compliance?: number; pushed?: number; ignored?: number;
    matchedInSf?: number;
    discrepancies?: number; accounts?: number;
  };
  lastScan: Record<string, any>;
  mapStats: Partial<BpMapStats>;
  /** A scan is in flight — poll for queue items as they land. */
  scanRunning: boolean;
  envs: Record<string, { configured: boolean; budgetRemaining: number }>;
}

export interface IfastReconTx {
  transactionId: string | null;
  bookingDate: string | null;
  code: string;
  /** Signed: credits positive, debits negative. */
  amount: number;
  currency: string;
  info: string;
}

export interface IfastReconAccount {
  name: string;
  iban: string;
  bankAccountNo: string;
  currency: string;
  balance: number;
}

export interface IfastReconItem extends IfastReconAccount {
  sfId: string;
  sheet: number;
  bank: number;
  /** Bank minus sheet. */
  diff: number;
  recent?: IfastReconTx[];
  explainedBy?: IfastReconTx[] | null;
  traceError?: string;
}

export interface IfastReconResult {
  sheet: string;
  copiedAt: string;
  ageMinutes: number | null;
  stale: boolean;
  bankAsAt: string;
  parseErrors: string[];
  traceDepth: number;
  tracingSkipped: boolean;
  matched: IfastReconItem[];
  breaks: IfastReconItem[];
  unknown: { sfId: string; balance: number }[];
  missing: IfastReconAccount[];
  explained: number;
}

export interface IfastStatementAccount {
  iban: string;
  name: string;
  currency: string;
  bankAccountNo: string;
  sortCode: string;
}

export interface IfastStatementExport {
  id: string;
  status: "running" | "cancelling" | "done" | "failed" | "cancelled";
  createdBy: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  dateFrom: string;
  dateTo: string;
  scope: "all" | "selected";
  accountCount: number;
  done: number;
  current: string | null;
  transactions: number;
  failed: { name: string; iban: string; error: string }[];
  paused: string | null;
  error: string | null;
  filename: string | null;
  fileSize: number | null;
  downloadable: boolean;
}

export interface BpExclusion {
  accountId: string;
  accountName: string | null;
  reason: string | null;
  addedAt: string | null;
  addedBy: string | null;
  /** Banks the exclusion applies to ("uk", "ie", "ifast"); null = all. */
  banks: string[] | null;
}

export interface BpCounterparty {
  identifier: string;
  bic: string | null;
  payerName: string | null;
  bankName: string | null;
  creditInstitutionId: string | null;
  creditInstitutionName: string | null;
  thirdPartyLinks: number;
  timesSeen: number;
  lastSeenAt: string | null;
  confirmedAt: string | null;
}

export interface BpMapStats {
  counterparties: number;
  withCreditInstitution: number;
  thirdPartyLinks: number;
}

export interface BpAuditEntry {
  id: number;
  at: string | null;
  user: string;
  action: string;
  queueItemId: string | null;
  detail: Record<string, any>;
}

export interface BpLookupResult {
  id: string;
  name: string;
  /** Third parties: approval status. Bank accounts: currency name. */
  status?: string;
  currencyId?: string;
  accountId?: string;
  accountName?: string;
}

export interface BookingTrade {
  id: string; ref: string; accountId: string | null; accountName: string | null; pm: string | null;
  tradeType: string | null; status: string | null; incomingStatus: string; outgoingStatus: string;
  sellCurrency: string | null; buyCurrency: string | null; sellAmount: number | null; buyAmount: number | null;
  clientRate: number | null; brokerRate: number | null; inverseRate: boolean;
  lpSellAmount: number | null; lpBuyAmount: number | null; profit: number | null; profitGbp: number | null;
  conversionRate: number | null; lpName: string | null; lpReference: string | null;
  valueDate: string | null; farDate: string | null; tradeDate: string | null; createdAt: string | null; lastModifiedAt: string | null;
  editable: boolean; lockedReason: string | null;
}

export interface TeamPulseTrader {
  traderId: string; traderName: string;
  revenueMtd: number; revenueLastMonthToDate: number; revenueLastMonth: number; momPct: number | null;
  tradesMtd: number; clientsTradedMtd: number; revenueYtd: number;
  newClientsQtd: number; newClientsYtd: number;
  lastNewClientName: string | null; lastNewClientDate: string | null; daysSinceNewClient: number | null;
  activeClients: number;
}
export interface TeamPulse { asOf: string; loadedAt: string; mtdStart: string; lastMonthCutoff: string; quarterStart: string; traders: TeamPulseTrader[] }
export interface LeadActivityPm {
  pmId: string; pmName: string; callsLogged: number; followUpsSet: number; assigned: number;
  pushedToSf: number; closed: number; pipeline: number; working: number; overdue: number;
  callsByDay: Array<{ date: string; calls: number }>;
}
export interface LeadActivity { days: number; pms: LeadActivityPm[] }

export interface MorningBriefing {
  briefingDate: string;
  status: "pending" | "ready" | "failed";
  content: string | null;
  error: string | null;
  model: string | null;
  generatedAt: string | null;
  priceAction: Array<{ pair: string; session: string; close: number; change_pct: number | null }> | null;
}

// ── Risk management ────────────────────────────────────────────
// Mirrors app/services/risk.py. mtm sign: + client onside, - client
// offside (= Millbank credit exposure). Exposure = Sell_Amount_Left__c.
export interface RiskPosition {
  id: string; ref: string; accountId: string | null; accountName: string | null;
  pmId: string | null; pm: string; pair: string | null;
  sellCurrency: string | null; buyCurrency: string | null;
  sellAmount: number; buyAmount: number; remaining: number;
  exposureGbp: number | null; contractRate: number | null; contractRateSource: "broker" | "broker_unoriented" | "implied";
  marketRate: number | null; marketMid: number | null; clientRate: number | null; brokerRate: number | null; obligationBuy: number | null;
  mtm: number | null; mtmGbp: number | null; mtmMidGbp: number | null; mtmPct: number | null;
  realisedGbp: number | null; drawdowns: number; pnlGbp: number | null;
  side: "onside" | "offside" | null;
  nearDate: string | null; farDate: string | null; daysToFar: number | null;
  tradeDate: string | null; status: string | null;
  /** Where marketRate came from: Velocity's revaluation for the date, or live market. */
  marketSource?: "velocity" | "market" | null;
}
export interface RiskBucket {
  key: string; label: string; positions: number; exposureGbp: number; mtmGbp: number;
  offsideGbp: number; onsideGbp: number; offsidePositions: number; unpriced: number;
  shareOfBookPct: number; mtmPctOfExposure: number | null;
  pnlGbp: number; remaining: number; sellCurrency: string | null; marketRate: number | null;
}
export interface RiskTotals {
  positions: number; priced: number; unpriced: number; mtmMidGbp: number; spreadCostGbp: number;
  realisedGbp: number; pnlGbp: number; drawdownLegs: number; clients: number; pms: number; pairs: number;
  exposureGbp: number; mtmGbp: number; offsideGbp: number; onsideGbp: number;
  offsidePositions: number; mtmPctOfExposure: number | null;
  velocityPriced?: number; marketPriced?: number;
}
export interface RiskStressBand {
  bandPct: number;
  adverse: { mtmGbp: number; offsideGbp: number; offsidePositions: number; clientsOffside: number };
  favourable: { mtmGbp: number };
  byClient: Array<{ accountId: string; accountName: string | null; mtmGbp: number; offsideGbp: number; exposureGbp: number; mtmPctOfExposure: number | null }>;
  byPair: Array<{ pair: string; mtmGbp: number; offsideGbp: number; exposureGbp: number; positions: number }>;
}
export interface RiskOverview {
  asOf: string; totals: RiskTotals; byClient: RiskBucket[]; byPm: RiskBucket[]; byPair: RiskBucket[];
  stress: { bands: number[]; results: RiskStressBand[] };
  positions: RiskPosition[];
  notes: Record<string, string>;
  revaluationFile?: VelocityFileInfo | null;
}

/** A row left out of a payroll upload, and why. */
export interface MpDroppedRow {
  sourceFile: string;
  rowNum: number | null;
  name: string;
  amount: number | null;
  sortCode: string;
  accountNumber: string;
  reasons: string[];
}

export interface VelocityFileInfo { id: string; name: string; source: string; rows: number; receivedAt: string | null }
export interface VelocityFileRow { id: string; name: string; kind: "positions" | "revaluation"; source: string; receivedAt: string | null; rows: number; uploadedBy: string | null }
export interface MarginClient {
  accountId: string | null; accountName: string; pm: string | null; positions: number;
  grossMarginUsd: number; grossMarginGbp: number | null; grossSharePct: number | null;
  standaloneUsd: number; standaloneGbp: number | null; standalonePctOfNet: number | null;
  heldUsd?: number | null; heldGbp?: number | null;
}
export interface MarginCurrency { currency: string; marginUsd: number; marginGbp: number | null; side: "long" | "short" | "flat"; pct: number | null }
export interface RiskMargin {
  positionsFile: VelocityFileInfo | null; revaluationFile: VelocityFileInfo | null; message?: string;
  margin: null | {
    netMarginUsd: number; netMarginGbp: number | null; totalLongsUsd: number; totalShortsUsd: number; grossMarginUsd: number;
    positions: number; linkedPositions: number; currencies: MarginCurrency[]; clients: MarginClient[];
    unpricedCurrencies: string[]; missingRateCurrencies: string[]; method: string; usdPerGbp: number;
    heldUsd?: number; heldGbp?: number | null; headroomUsd?: number; headroomGbp?: number | null; scopedToPm?: boolean;
  };
}

// ── MFX Hub ────────────────────────────────────────────────────
// Mirrors app/services/hub.py. Widget `type` must be a key of the
// catalogue in src/lib/hub.ts.
// Width in columns of a 6-column grid (2 = third, 3 = half, 4 = two
// thirds, 6 = full) and height in row units.
export interface HubWidgetSize { w: 2 | 3 | 4 | 6; h: 1 | 2 | 3 }
export interface HubWidget {
  id: string;
  type: string;
  size: HubWidgetSize;
  settings: Record<string, any>;
}
export interface HubQuickNav { label: string; to: string }
export interface HubLayout { widgets: HubWidget[]; quickNav: HubQuickNav[] }
export interface HubLayoutResponse {
  layout: HubLayout;
  isDefault: boolean;
  updatedAt: string | null;
}
export interface HubBankPaymentRow {
  id: string; ref: string; accountId: string | null; accountName: string | null;
  amount: number | null; currency: string | null; status: string | null;
  valueDate: string | null; thirdParty: string | null; createdAt: string | null;
}
export interface HubTradeRow {
  id: string; ref: string; accountId: string | null; accountName: string | null;
  buyCurrency: string | null; sellCurrency: string | null;
  buyAmount: number | null; sellAmount: number | null; rate: number | null;
  status: string | null; tradeType: string | null; valueDate: string | null;
  tradeDate: string | null; farDate: string | null; nearDate: string | null;
  trader: string | null; createdAt: string | null;
}
export interface HubPaymentRow {
  id: string; ref: string; accountId: string | null; accountName: string | null;
  amount: number | null; amountGbp: number | null; currency: string | null;
  status: string | null; valueDate: string | null; dueDate: string | null;
  beneficiary: string | null; tradeId: string | null; createdAt: string | null;
}

export interface OrphanRow {
  id: string;
  sourceFileId: string | null;
  sourcePaymentIndex: number | null;
  sourceFilename: string | null;
  sfAccountId: string;
  bankKind: "ifast" | "freemarket";
  // Editable parsed_row — sort_code, account_number, first_name, etc.
  parsedRow: Record<string, any>;
  sfBeneficiaryId: string | null;
  originCategory: string | null;
  originCategoryNote: string | null;
  originIssueItemId: string | null;
  staffNote: string | null;
  consumedAt: string | null;
  consumedInFileId: string | null;
  sfUpdatedAt: string | null;
  // Chase tracking — set when staff have emailed the payroll company
  // about this payee's bad bank details. Null = still needs an email.
  clientEmailedAt: string | null;
  createdAt: string | null;
  createdByUsername: string | null;
  updatedAt: string | null;
  updatedByUsername: string | null;
  // Convenience top-level mirrors of parsed_row for table display.
  displayName: string;
  accountNumber: string;
  sortCode: string;
  amount: number;
  reference: string;
}

export interface NotifRecipient {
  account_id: string;
  name: string;
  country: string;
}

export interface NotifGroup {
  id: string;
  name: string;
  description: string;
  account_ids: string[];
  member_count: number;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface NotifAudienceDefinition {
  account_ids?: string[];
  group_ids?: string[];
  countries?: string[];
  currency?: { code: string; days: number } | null;
  exclude_account_ids?: string[];
}

export interface NotifCampaign {
  id: string;
  subject: string;
  body: string;
  channels: string[];
  recipient_count: number;
  status: string;
  counts: Record<string, { sent?: number; failed?: number; skipped?: number }>;
  created_by?: string | null;
  created_at?: string | null;
  sent_at?: string | null;
  email_header_url?: string | null;
}

// ── Email (Notifications composer) ─────────────────────────────

export interface EmailStatus {
  mode: "off" | "allowlist" | "live";
  allowlist: string[];
  from: string;
  staging: boolean;
  key_set: boolean;
}

export interface EmailHeaderImage {
  name: string;
  label: string;
  url: string;
  is_default: boolean;
  created_at?: string | null;
  size?: number | null;
}

export interface EmailDraft {
  subject: string;
  body: string;
  header_url?: string | null;
}

// ── Dev Zone: reference extractor ──────────────────────────────

export interface ExtractorPackageInfo {
  runId?: string;
  packagedAt?: string;
  trainedFrom?: string;
  testLabels?: number;
  uncompressedAccuracy?: number;
  packagedAccuracy?: number;
  sameAnswer?: number;
  msPerPaymentOnMac?: number;
  modelBytes?: number;
  report?: {
    sameRows?: { rows: number; new: number | null; current: number | null;
                 newPerPayer: number | null; currentPerPayer: number | null };
    allWithReference?: { rows: number; new: number | null; current: number | null;
                         newPerPayer: number | null; currentPerPayer: number | null };
  };
}

export interface ExtractorStatus {
  active: {
    id: number;
    meta: { baseModel?: string; package?: ExtractorPackageInfo };
    sizeBytes: number | null;
    uploadedAt: string | null;
  } | null;
  process: {
    modelId: number | null;
    activeId: number | null;
    loading: boolean;
    error: string | null;
    ready: boolean;
    loadedAt: string | null;
  };
  history: {
    id: number; active: boolean; sizeBytes: number | null; uploadedAt: string | null;
    runId: string | null; packagedAccuracy: number | null;
  }[];
}

// ── Dev Zone: label clean-up ───────────────────────────────────

export interface LabelReviewRow {
  id: number;
  current: string;
  line: string;
  identifier: string | null;
}

export interface LabelFillerReport {
  labelsChecked: number;
  /** Labels mixing a reference with wording — the ones breaking the rule. */
  withFiller: number;
  trimCanFix: number;
  needsHand: number;
  /** Description-only labels. Valid under the rule, counted for context. */
  descriptive?: number;
  topWords: { word: string; count: number; inVocabulary: boolean }[];
}

// ── Dev Zone: historic training ────────────────────────────────

export interface HistoricBankAccount {
  id: string;
  name: string;
  clientName: string;
  currency: string;
  fmAccountId: string;
  env: string;
  iban: string;
}

export interface HistoricFunnel {
  bankPayments: number;
  depositsFetched: number;
  withInvoiceNumber: number;
  alreadyLinked: number;
  uniqueAmountSf: number;
  matchedOneToOne: number;
  dateWithinTolerance: number;
  alreadyLabelled: number;
  multiReference: number;
  appearsInNarrative: number;
  reachableSpan: number;
  reachableAfterTrim: number;
  usable: number;
}

export interface HistoricPair {
  bankPaymentId: string;
  bankPaymentName: string;
  fmTransactionId: string;
  amount: number;
  currency: string;
  sfValueDate: string;
  fmValueDate: string;
  payer: string;
  identifier: string;
  fmReference: string;
  narrative: string[];
  invoiceNumber: string;
  trimmed: string;
  verbatim: boolean;
  reachable: boolean;
  trimmedReachable: boolean;
  multiReference: boolean;
  usable: boolean;
  label: string;
}

export interface HistoricPreview {
  bank: HistoricBankAccount;
  funnel: HistoricFunnel;
  maxTokens: number;
  months: number;
  toleranceDays: number;
  wouldImport: number;
  sample: HistoricPair[];
  budgetRemaining: number;
}

// ── Leads: self-sourced (PM-created) ───────────────────────────

export interface SelfSourcedLeadsPm {
  pmId: string | null;
  pmName: string;
  leadCount: number;
  inSf: number;
  terminal: number;
  totalEstRev: number;
  byDisplayStage: Record<string, { leadCount: number; totalEstRev: number }>;
}

export interface SelfSourcedLeadsSummary {
  totals: {
    leadCount: number;
    inSf: number;
    terminal: number;
    deleted: number;
    totalEstRev: number;
  };
  perPm: SelfSourcedLeadsPm[];
}

// ── Leads: batch management ────────────────────────────────────
// One batch = one lead_imports row = one file upload.

export interface LeadBatchDeletion {
  deletionId: string;
  importId: string;
  filename: string;
  leadCount: number;
  counts: Record<string, number>;
  excludedStages: string[];
  includedPushedToSf: boolean;
  deletedAt: string | null;
  deletedByName: string | null;
  restoredAt: string | null;
  restoredByName: string | null;
  restoredCount: number | null;
  isRestorable: boolean;
}

/** Slim summary of the open (un-restored) deletion on a batch row. */
export interface LeadBatchOpenDeletion {
  deletionId: string;
  leadCount: number;
  deletedAt: string | null;
  deletedByName: string | null;
  excludedStages: string[];
  includedPushedToSf: boolean;
}

export interface LeadBatchStageRow {
  stage: string;
  count: number;
  estRevenue: number;
  isPmHeld: boolean;
  isPushedToSf: boolean;
}

export interface LeadBatch {
  importId: string;
  filename: string;
  uploadedByName: string | null;
  createdAt: string | null;
  rowsTotal: number;
  rowsImported: number;
  rowsSkipped: number;
  skippedAccountNames: string[];
  activeCount: number;
  deletedCount: number;
  pushedToSfCount: number;
  pmHeldCount: number;
  unassignedCount: number;
  activeEstRevenue: number;
  /** Present only on the detail fetch. */
  byStage?: LeadBatchStageRow[];
  restorableDeletion: LeadBatchOpenDeletion | null;
}

export interface LeadBatchLead {
  id: string;
  accountName: string | null;
  stage: string;
  sfStatus: string | null;
  sfLeadId: string | null;
  countryOfOperation: string | null;
  estimatedAnnualRevenue: number | null;
  assignedPmName: string | null;
  contactCount: number;
  isDeleted: boolean;
  deletedAt: string | null;
}

export interface LeadBatchDeletePreview {
  importId: string;
  filename: string;
  toDelete: number;
  toSkip: number;
  activeCount: number;
  pushedToSfCount: number;
  pushedToSfIncluded: boolean;
  pmHeldCount: number;
  excludedStages: string[];
  byStage: (LeadBatchStageRow & {
    willDelete: boolean;
    skipReason: "excluded" | "pushed_to_sf" | null;
  })[];
  sampleAccountNames: string[];
}

// ── Compliance Tools: REP017 ────────────────────────────────────

export interface Rep017Row {
  service: "credit_transfer" | "money_remittance";
  serviceLabel: string;
  region: "domestic" | "eea" | "row";
  regionLabel: string;
  count: number;
  valueGbp: number;
}

export interface Rep017Totals {
  count: number;
  valueGbp: number;
}

/** Identifying detail for a payment that was left out of the report. */
export interface Rep017Sample {
  paymentId: string | null;
  paymentRef: string | null;
  accountName: string;
  beneficiaryName: string;
  currency: string;
  amount: number | null;
  createdDate: string | null;
  countryName?: string;
}

export interface Rep017Bucket {
  count: number;
  samples: Rep017Sample[];
}

export interface Rep017CountryRow {
  region: "domestic" | "eea" | "row";
  regionLabel: string;
  iso3: string;
  countryName: string;
  count: number;
  valueGbp: number;
}

export interface Rep017Report {
  startDate: string;
  endDate: string;
  periodStartUtc: string;
  periodEndUtc: string;
  timezone: string;
  generatedAt: string;
  excludedAccountIds: string[];
  excludedAccounts: Array<{ id: string; name: string }>;
  excludedBankIds: string[];
  excludedBanks: Array<{ id: string; name: string }>;
  /** Where the ISO alpha-3 came from: the Country record, the Beneficiary
   *  record, or "name" when neither field was queryable and countries had
   *  to be matched on their name. */
  isoCodeSource: "country" | "beneficiary" | "name";
  /** False when no ISO_Code_3__c field was queryable and the report fell
   *  back to matching country names. */
  isoCodeFieldAvailable: boolean;
  rows: Rep017Row[];
  serviceTotals: Record<string, Rep017Totals>;
  regionTotals: Record<string, Rep017Totals>;
  grandTotal: Rep017Totals;
  countries: Rep017CountryRow[];
  reconciliation: {
    scanned: number;
    included: number;
    dropped: number;
    balances: boolean;
  };
  exclusions: {
    excludedAccount: Rep017Bucket;
    excludedBank: Rep017Bucket;
    noBankAccount: Rep017Bucket;
    noBeneficiaryCountry: Rep017Bucket;
    unresolvedCountry: Rep017Bucket;
    noGbpValue: Rep017Bucket;
  };
  countriesMissingIsoCode: Array<{ countryName: string; paymentCount: number }>;
  /** CSV rendered server-side from this exact payload, so the download and
   *  the screen are the same numbers by construction. */
  csv: string;
  csvFilename: string;
}

export interface Rep017Bank {
  id: string;
  name: string;
}

export interface Rep017Period {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
}

// ── Reporting (plain-English questions -> SOQL) ─────────────────
export type ReportingChart = { type: "bar" | "line" | "pie"; x: string; y: string[]; series?: string; stacked?: boolean } | null;
export type ReportingPreStep = { id: string; soql: string; purpose?: string };
export type ReportingOutputSpec = {
  base?: string; first_per?: string[]; columns?: string[]; labels?: Record<string, string>;
  joins?: Array<{ from: string; base_key: string; key: string; columns: string[]; pick: "first" | "all" }>;
} | null;
export type ReportingStepInfo = { id: string; purpose?: string | null; soqlRun: string; rowCount: number; truncated: boolean };
export type ReportingTurn = {
  id: string; seq: number; userMessage: string;
  kind: "result" | "clarify" | "error";
  soql: string | null; soqlRun: string | null; explanation: string | null;
  preSteps: ReportingPreStep[]; stepInfo: ReportingStepInfo[]; output: ReportingOutputSpec;
  assumptions: string[]; chart: ReportingChart;
  message: string | null; options: string[]; lookups: string[]; templateId: string | null;
  errorRaw: string | null;
  rowCount: number | null; truncated: boolean | null; createdAt: string | null;
};
export type ReportingResult = {
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, unknown>>;
  rowCount: number; single: boolean; truncated: boolean; totalSize: number; note?: string;
};
export type ReportingTemplate = {
  id: string; name: string; mine: boolean; shared: boolean; ownerName: string | null; question: string; soql: string; preSteps: ReportingPreStep[];
  output: ReportingOutputSpec;
  explanation: string | null; assumptions: string[]; chart: ReportingChart;
  createdAt: string | null; updatedAt: string | null;
};
/** How a report widget shows its answer on the Hub. */
export type ReportingWidgetView =
  | { display: "table" | "figures" }
  | { display: "bar" | "line" | "pie"; x: string; y: string[]; series: string | null; stacked: boolean };
export type ReportingWidget = {
  id: string; title: string; question: string | null;
  /** null = only when refreshed by hand. Otherwise 60, 120, 240, 360, 720 or 1440. */
  refreshMinutes: number | null;
  view: ReportingWidgetView;
  result: ReportingResult | null; lastRunAt: string | null; lastError: string | null;
  stale: boolean; refreshing: boolean; note: string | null;
};
export type ReportingConversationSummary = {
  id: string; title: string; turns: number; updatedAt: string; shared: boolean; mine: boolean; ownerName: string | null;
};
export type ReportingSchemaView = {
  objects: Array<{ name: string; label: string; otherFields?: number; fields: Array<{ name: string; type: string; meaning: string }> }>;
  missing: string[]; model: string;
};

export const api = {
  // Raw axios client — escape hatch for callers that need response
  // headers (e.g. Content-Disposition for downloads) or other low-level
  // axios features. Prefer the named methods on this object first.
  client,

  // ── Account selector ───────────────────────
  getAccountSelector: () =>
    client
      .get<{ id: string; name: string; clientCode: string }[]>(
        "/accounts/selector"
      )
      .then((r) => r.data),

  // ── Dashboard ──────────────────────────────
  getDashboard: (accountId: string) =>
    client
      .get<DashboardSummary>("/dashboard", {
        params: { accountId },
      })
      .then((r) => r.data),

  // ── Accounts / Balances ────────────────────
  getAccounts: (accountId: string) =>
    client
      .get<Account[]>("/accounts", { params: { accountId } })
      .then((r) => r.data),

  getAccount: (accountId: string, ccy: string) =>
    client
      .get<AccountDetail>(`/accounts/${ccy}`, { params: { accountId } })
      .then((r) => r.data),

  getBalanceHistory: (
    accountId: string,
    ccy: string,
    period: "7d" | "30d" | "90d" = "30d"
  ) =>
    client
      .get<BalanceHistoryPoint[]>(`/accounts/${ccy}/balance-history`, {
        params: { accountId, period },
      })
      .then((r) => r.data),

  // ── Trades ─────────────────────────────────
  getTrades: (accountId: string, filters?: TradeFilters) =>
    client
      .get<PaginatedResponse<Trade>>("/trades", {
        params: { accountId, ...filters },
      })
      .then((r) => r.data),

  getTrade: (tradeId: string) =>
    client.get<TradeDetail>(`/trades/${tradeId}`).then((r) => r.data),

  // ── Transactions ───────────────────────────
  getTransactions: (accountId: string, filters?: TransactionFilters) =>
    client
      .get<PaginatedResponse<Transaction>>("/transactions", {
        params: { accountId, ...filters },
      })
      .then((r) => r.data),

  getTransaction: (txId: string) =>
    client.get<TransactionDetail>(`/transactions/${txId}`).then((r) => r.data),

  // ── Statements ─────────────────────────────
  getStatements: (accountId: string) =>
    client
      .get<Statement[]>("/statements", { params: { accountId } })
      .then((r) => r.data),

  // Distinct currencies this account has ever traded — drives the
  // single-currency statement options so clients only see currencies
  // relevant to them.
  getStatementCurrencies: (accountId: string) =>
    client
      .get<{ currencies: string[] }>("/statements/currencies", { params: { accountId } })
      .then((r) => r.data.currencies),

  generateStatement: (req: StatementRequest) =>
    client.post<Statement>("/statements/generate", req).then((r) => r.data),

  downloadStatement: (stmtId: string) =>
    client.get(`/statements/${stmtId}/download`, { responseType: "blob" }),

  // ── Profile ────────────────────────────────
  getProfile: (accountId: string) =>
    client
      .get<ClientProfile>("/profile", { params: { accountId } })
      .then((r) => r.data),

  // ── Beneficiaries ──────────────────────────
  getBeneficiaries: (accountId: string, search?: string) =>
    client
      .get("/beneficiaries", { params: { accountId, search: search || undefined } })
      .then((r) => r.data),

  getBeneficiary: (accountId: string, benefId: string) =>
    client
      .get(`/beneficiaries/${benefId}`, { params: { accountId } })
      .then((r) => r.data),

  // ── Third Parties ──────────────────────────
  getThirdParties: (accountId: string, search?: string) =>
    client
      .get("/third-parties", { params: { accountId, search: search || undefined } })
      .then((r) => r.data),

  getThirdParty: (accountId: string, tpId: string) =>
    client
      .get(`/third-parties/${tpId}`, { params: { accountId } })
      .then((r) => r.data),

  // ── Hedging Calendar ───────────────────────
  getHedgingCalendar: (accountId: string) =>
    client
      .get("/hedging-calendar", { params: { accountId } })
      .then((r) => r.data),

  // ── Commissions ────────────────────────────
  getCommissions: (sfUserId: string, params?: { month?: number; year?: number; dateFrom?: string; dateTo?: string }) =>
    client
      .get("/commissions", { params: { sfUserId, ...params } })
      .then((r) => r.data),

  // ── Admin ──────────────────────────────────
  getAdminUsers: () =>
    client
      .get("/auth/admin/users")
      .then((r) => r.data),

  // ── Documents ──────────────────────────────
  getDocuments: (entityType: "payment" | "trade", entityId: string) =>
    client
      .get(`/documents/${entityType}/${entityId}`)
      .then((r) => r.data),

  downloadDocument: async (versionId: string, filename: string) => {
    const resp = await client.get(`/documents/download/${versionId}`, {
      responseType: "blob",
    });
    const blob = new Blob([resp.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  getDocumentDownloadUrl: (versionId: string) =>
    `${client.defaults.baseURL}/documents/download/${versionId}`,

  downloadCounterpartyStatement: async (kind: "beneficiaries" | "third-parties", accountId: string, id: string, name?: string) => {
    const resp = await client.get(`/${kind}/${id}/statement`, {
      params: { accountId },
      responseType: "blob",
      timeout: 120_000,
    });
    const cd = resp.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `statement-${name || id}.pdf`;
    const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  downloadPaymentConfirmation: async (paymentId: string) => {
    const resp = await client.get(`/payments/${paymentId}/confirmation`, {
      responseType: "blob",
    });
    const cd = resp.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `payment-confirmation-${paymentId}.pdf`;
    const url = URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  downloadTradeConfirmation: async (tradeId: string) => {
    const resp = await client.get(`/trades/${tradeId}/confirmation`, {
      responseType: "blob",
    });
    const cd = resp.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `trade-confirmation-${tradeId}.pdf`;
    const url = URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ── Messages ───────────────────────────────
  sendMessage: (params: { accountId: string; message: string; contextType?: string; contextId?: string; contextLabel?: string }) =>
    client
      .post("/auth/messages/send", params)
      .then((r) => r.data),

  getMessages: (accountId: string, before?: string) =>
    client
      .get(`/auth/messages/${accountId}`, { params: { before } })
      .then((r) => r.data),

  getConversations: () =>
    client
      .get("/auth/messages/conversations/list")
      .then((r) => r.data),

  getUnreadCount: (accountId: string) =>
    client
      .get("/auth/messages/unread/count", { params: { accountId } })
      .then((r) => r.data),

  // ── Market Intelligence ────────────────────
  getMarketCandles: (symbol: string, timeframe: string, limit = 250) =>
    client
      .get("/market/candles", { params: { symbol, timeframe, limit } })
      .then((r) => r.data as {
        symbol: string;
        timeframe: string;
        displayCount: number;
        candles: Array<{ t: number; o: number; h: number; l: number; c: number; v: number }>;
      }),

  getMarketQuote: (symbol: string) =>
    client
      .get("/market/quote", { params: { symbol } })
      .then((r) => r.data as { symbol: string; bid: number; ask: number; mid: number; ts: number }),

  searchMarketPairs: (q: string) =>
    client
      .get("/market/search", { params: { q } })
      .then((r) => r.data as { query: string; pairs: Array<{ symbol: string; label: string }> }),

  getEconomicCalendar: (weekOffset?: number) =>
    client
      .get("/market/calendar", { params: { weekOffset: weekOffset ?? 0 } })
      .then((r) => r.data),

  generateCalendarDescriptions: () =>
    client
      .post("/market/calendar/generate-descriptions")
      .then((r) => r.data),

  saveCalendarDescriptions: (descriptions: any[]) =>
    client
      .post("/market/calendar/save-descriptions", { descriptions })
      .then((r) => r.data),

  // Bank account visibility
  getBankAccountsForClient: (sfAccountId: string) =>
    client.get(`/bank-accounts/${sfAccountId}`).then((r) => r.data),

  toggleBankAccountVisibility: (sfAccountId: string, bankAccountId: string, enabled: boolean) =>
    client
      .post(`/bank-accounts/${sfAccountId}/toggle`, { bankAccountId, enabled })
      .then((r) => r.data),

  // Mass Payments visibility (per-account toggle, staff-only PATCH)
  getMassPaymentsAccess: (accountId: string) =>
    client
      .get("/mass-payments/access", { params: { accountId } })
      .then(
        (r) =>
          r.data as { hasAccess: boolean; canSeeFiles: boolean; enabled: boolean },
      ),

  // ── Client-Facing page visibility ──────────────────────────
  getPageVisibility: () =>
    client.get("/admin/page-visibility").then((r) => r.data as {
      pages: Array<{
        key: string; label: string; group: string; path: string;
        audience: "client" | "internal" | "vertical";
        locked: boolean; clientFacing: boolean;
      }>;
    }),

  setPageVisibility: (pageKey: string, clientFacing: boolean) =>
    client
      .put(`/admin/page-visibility/${pageKey}`, { clientFacing })
      .then((r) => r.data as { key: string; clientFacing: boolean }),

  getMyClientFacingPages: () =>
    client.get("/me/client-facing-pages").then((r) => r.data as {
      paths: string[]; internal: boolean;
    }),

  getLendingAssets: () =>
    client.get("/lending/assets").then((r) => r.data as {
      brochure: { available: boolean; url: string | null };
      headshot: { available: boolean; url: string | null };
    }),

  // ── Client Documents (secure POI/POA upload) ──
  uploadMyDocument: (file: File, docType: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("docType", docType);
    return client.post("/me/documents", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    }).then((r) => r.data as ClientDocument);
  },
  listMyDocuments: () =>
    client.get("/me/documents").then((r) => r.data as { documents: ClientDocument[] }),
  myDocumentDownloadUrl: (id: string) =>
    client.get(`/me/documents/${id}/download`).then((r) => r.data as { url: string }),
  deleteMyDocument: (id: string) =>
    client.delete(`/me/documents/${id}`).then((r) => r.data as { ok: boolean }),
  listClientDocuments: (accountId?: string) =>
    client.get("/admin/client-documents", {
      params: accountId ? { account_id: accountId } : {},
    }).then((r) => r.data as { documents: ClientDocument[] }),
  clientDocumentDownloadUrl: (id: string) =>
    client.get(`/admin/client-documents/${id}/download`).then((r) => r.data as { url: string }),
  deleteClientDocument: (id: string) =>
    client.delete(`/admin/client-documents/${id}`).then((r) => r.data as { ok: boolean }),

  toggleMassPaymentsVisibility: (sfAccountId: string, enabled: boolean) =>
    client
      .patch(`/accounts/${sfAccountId}/mass-payments-visibility`, { enabled })
      .then(
        (r) =>
          r.data as { success: boolean; accountId: string; enabled: boolean },
      ),

  getVisibleBankAccounts: (accountId?: string) =>
    client.get("/visible-bank-accounts", { params: accountId ? { accountId } : {} }).then((r) => r.data),

  getReferralCount: () =>
    client.get("/referrals/count").then((r) => r.data),

  // ── Mass Payments ─────────────────────────
  getMassPaymentsUploadRecent: (accountId: string) =>
    client.get("/mass-payments/upload/recent", { params: { accountId } }).then((r) => r.data as {
      uploads: Array<{
        fileId: string;
        filename: string;
        status: string;
        bankKind: "ifast" | "freemarket";
        paymentCount: number | null;
        totalAmountGbp: number;
        newBeneficiariesCount: number | null;
        paymentDate: string | null;
        uploadedAt: string | null;
      }>;
    }),

  postMassPaymentsRetryEnrolment: (fileId: string) =>
    client.post(`/mass-payments/files/${fileId}/retry-enrolment`).then((r) => r.data as {
      retried: number;
      counts: { created: number; already_exists: number; failed: number };
    }),
  getMassPaymentsWorkbenchItems: (accountId: string) =>
    client.get("/mass-payments/workbench/items", { params: { accountId } }).then((r) => r.data as {
      items: Array<{
        fileId: string;
        filename: string;
        status: string;
        bankKind: "ifast" | "freemarket";
        paymentCount: number | null;
        totalAmountGbp: number;
        newBenefsCount: number | null;
        changedDetailsCount: number | null;
        anomalousCount: number;
        anomalousResolvedCount: number;
        screeningTotal: number;
        screeningCompleted: number;
        screeningPassed: number;
        screeningFlagged: number;
        // Ongoing monitoring enrolment (Facctum entity register).
        monitoringEnrolment: { created: number; already_exists: number; failed: number } | null;
        paymentDate: string | null;
        uploadedAt: string | null;
        pendingAnomalous: Array<{
          index: number;
          benefName: string;
          amount: number;
          rollingAvg: number | null;
          multiple: number | null;
        }>;
      }>;
      counts: { invalidBeneficiary: number; failedPayment: number; other: number };
    }),

  getMassPaymentsHistory: (accountId: string) =>
    client.get("/mass-payments/history", { params: { accountId } }).then((r) => r.data as {
      runs: Array<{
        fileId: string;
        filename: string;
        status: string;
        bankKind: "ifast" | "freemarket";
        paymentCount: number | null;
        excludedCount: number;
        totalAmountGbp: number;
        paymentDate: string | null;
        markedPaidAt: string | null;
      }>;
    }),

  getMassPaymentsFiles: (params: {
    accountId?: string;
    scope: "client" | "all";
    paidFilter?: "pending" | "paid" | "all";
    stage?:
      | "all"
      | "in_progress"
      | "ready_to_pay"
      | "validated"
      | "sent_to_bank"
      | "paid_not_pushed"
      | "pushed_to_sf"
      | "failed";
    includeDeleted?: boolean;
    // Payment date range filter — applies across all stages.
    paymentDateFrom?: string;  // YYYY-MM-DD
    paymentDateTo?: string;    // YYYY-MM-DD
  }) =>
    client.get("/mass-payments/files", { params, timeout: 60_000 }).then((r) => r.data as {
      files: Array<{
        fileId: string;
        sfAccountId: string;
        accountName?: string;
        missingCityIndexes?: number[];
        missingCityCount?: number;
        hiddenExcludedIndexes?: number[];
        hiddenExcludedCount?: number;
        autoFilledBankNameIndexes?: number[];
        autoFilledBankNameCount?: number;
        unparseableExcludedIndexes?: number[];
        unparseableExcludedCount?: number;
        /** Rows left out of the upload; the file can't be paid until acknowledged. */
        droppedRows?: MpDroppedRow[];
        droppedCount?: number;
        droppedTotal?: number;
        droppedAcknowledged?: boolean;
        droppedAckAt?: string | null;
        droppedAckBy?: string | null;
        droppedAckNote?: string | null;
        filename: string;
        status: string;
        bankKind: "ifast" | "freemarket";
        paymentCount: number | null;
        excludedCount: number;
        totalAmountGbp: number;
        pushedCount?: number | null;
        pushedAmountGbp?: number | null;
        pushState?: string | null;
        pushDone?: number | null;
        pushTotal?: number | null;
        pushError?: string | null;
        anomalousCount: number;
        newBenefsCount: number | null;
        screeningTotal: number;
        paymentDate: string | null;
        uploadedAt: string | null;
        originalDownloadUrl: string | null;
        cleanedDownloadUrl: string | null;
        bankFileCount: number;
        bankFilesZipUrl: string;
        allZipUrl: string;
        // Per-row table for the mark-as-paid search-and-exclude modal.
        paymentRows: Array<{
          index: number;
          benefName: string;
          amount: number | null;
          reference: string;
          sortCode: string;
          accountNumber: string;
          excluded: boolean;
        }>;
        isDeleted?: boolean;
        deletedAt?: string | null;
        canDelete?: boolean;
        uploadedToBankAt?: string | null;
        freemarketBatchId?: string | null;
        ifastBulkPaymentFileId?: string | null;
        validatedAt?: string | null;
        // Issue items linked to this file (from the iFAST Validate
        // flow). Drive visibility of the without-issues / only-issues
        // bank-file exports — both are pointless when both are zero.
        flaggedCount?: number;
        unresolvedFlaggedCount?: number;
        staffNotes?: string | null;
        complianceFlagNote?: string | null;
        complianceFlaggedAt?: string | null;
        markedPaidAt?: string | null;
        pushedToSfAt?: string | null;
        duplicateOf?: Array<{
          fileId: string;
          filename: string;
          uploadedAt: string | null;
        }>;
      }>;
      scope: string;
      paidFilter: string;
      accountId: string | null;
    }),

  // POST a payload of N files + one shared payment date.
  // FormData; axios sets multipart Content-Type automatically.
  uploadMassPayments: (
    accountId: string,
    files: File[],
    paymentDate: string, // YYYY-MM-DD
    force = false, // skip same-filename duplicate warning
  ) => {
    const form = new FormData();
    form.append("accountId", accountId);
    form.append("paymentDate", paymentDate);
    if (force) form.append("force", "true");
    for (const f of files) form.append("files", f);
    return client
      .post("/mass-payments/upload", form, {
        // Larger timeout: Supabase Storage write + SF SOQL reconcile.
        timeout: 120_000,
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(
        (r) =>
          r.data as {
            summaries: Array<{
              fileId: string;
              filename: string;
              status: string;
              bankKind: "ifast" | "freemarket";
              // Present when status === 'duplicate_name': file wasn't
              // processed because the same filename already exists.
              duplicateOfFileId?: string;
              duplicateUploadedAt?: string | null;
              summary?: {
                paymentCount: number;
                totalAmountGbp: number;
                newBeneficiariesCount: number;
                existingWithChangedDetailsCount: number;
                anomalousPaymentsCount: number;
                paymentDate: string;
                issuesSummary: string[];
                droppedRows?: MpDroppedRow[];
                droppedCount?: number;
              };
              anomalousPayments?: Array<{
                index: number;
                benefName: string;
                amount: number;
                rollingAvg: number | null;
                multiple: number | null;
              }>;
              error?: string;
            }>;
          },
      );
  },

  // Release (include with confirmation) or exclude (drop from this run).
  acknowledgeMpDroppedRows: (fileId: string, note?: string) =>
    client.post(`/mass-payments/files/${fileId}/acknowledge-dropped`, { note })
      .then((r) => r.data as { fileId: string; droppedAcknowledged: boolean }),

  postMassPaymentsAnomalousDecision: (
    fileId: string,
    index: number,
    decision: "release" | "exclude",
  ) =>
    client
      .post(
        `/mass-payments/files/${fileId}/anomalous-payments/${index}/decision`,
        null,
        { params: { decision } },
      )
      .then(
        (r) =>
          r.data as {
            fileId: string;
            index: number;
            decision: string;
            remainingAnomalous: number;
          },
      ),

  postMassPaymentsMarkComplianceCleared: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/mark-compliance-cleared`)
      .then((r) => r.data as { fileId: string; status: string; beneficiariesApproved?: number;
        approvedNames?: string[]; failedNames?: string[] }),

  postMassPaymentsChangePaymentDate: (fileId: string, paymentDate: string) =>
    client
      .post(`/mass-payments/files/${fileId}/change-payment-date`, { paymentDate })
      .then((r) => r.data as { fileId: string; paymentDate: string; bankFiles: string[] }),

  postMassPaymentsBouncebackSearchGlobal: (body: { amount?: number | string; name?: string }) =>
    client.post(`/mass-payments/bounceback/search`, body).then((r) => r.data as {
      matches: Array<{
        fileId: string; fileName: string; paymentDate: string | null;
        sfAccountId: string | null; accountName: string; bankKind: string | null;
        paymentIndex: number; name: string; bankAccountName: string;
        amount: number | null; reference: string; sortCode: string; accountNumber: string;
        sfBeneficiaryId: string | null; alreadyOrphaned: boolean; bouncedBack: boolean;
      }>;
    }),

  postMassPaymentsBouncebackSearch: (fileId: string, body: { amount?: number | string; name?: string }) =>
    client.post(`/mass-payments/files/${fileId}/bounceback/search`, body).then((r) => r.data as {
      matches: Array<{
        paymentIndex: number; name: string; bankAccountName: string;
        amount: number | null; reference: string; sortCode: string; accountNumber: string;
        sfBeneficiaryId: string | null; alreadyOrphaned: boolean; bouncedBack: boolean;
      }>;
    }),

  postMassPaymentsBouncebackResolve: (fileId: string, paymentIndex: number) =>
    client.post(`/mass-payments/files/${fileId}/bounceback/resolve`, { paymentIndex }).then((r) => r.data as {
      payments: Array<{
        sfPaymentId: string; name: string; amount: number | null; reference: string | null;
        status: string; beneficiaryName: string | null;
        transactions: Array<{ sfTransactionId: string; amount: number | null }>;
      }>;
      note?: string;
    }),

  postMassPaymentsBouncebackExecute: (fileId: string, paymentIndex: number, sfPaymentId: string) =>
    client.post(`/mass-payments/files/${fileId}/bounceback/execute`, { paymentIndex, sfPaymentId }).then((r) => r.data as {
      sfPaymentId: string; cancelledPayment: boolean; deletedTransactionIds: string[]; orphanId: string | null;
    }),

  // Stage 2: bank file uploaded to the bank's portal.
  // Freemarket requires a Batch ID from the Freemarket platform; iFAST
  // optionally accepts the BP… Bulk Payment File ID assigned at upload.
  postMassPaymentsMarkUploaded: (
    fileId: string,
    body: { freemarketBatchId?: string; ifastBulkPaymentFileId?: string } = {},
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/mark-uploaded`, body)
      .then(
        (r) =>
          r.data as {
            fileId: string;
            status: string;
            freemarketBatchId: string | null;
            ifastBulkPaymentFileId: string | null;
          },
      ),

  // iFAST Validate flow: transition file to `validated` and (optionally)
  // append rows with iFAST validation issues to an issue report.
  validateMassPaymentsFile: (
    fileId: string,
    body: {
      items: Array<{
        paymentIndex: number;
        category: "compliance_request" | "wrong_account_number" | "wrong_sort_code" | "custom";
        note?: string;
      }>;
      reportId?: string;
      newReportName?: string;
    },
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/validate`, body)
      .then(
        (r) =>
          r.data as {
            fileId: string;
            status: string;
            reportId: string | null;
            itemsAdded: number;
          },
      ),

  // List a client's issue reports — used by the "add to existing
  // report" picker in the Validate modal.
  listMassPaymentsIssueReports: (sfAccountId: string) =>
    client
      .get(`/mass-payments/accounts/${sfAccountId}/issue-reports`)
      .then(
        (r) =>
          r.data as {
            reports: Array<{
              id: string;
              name: string;
              notionalDate: string | null;
              createdAt: string | null;
              closedAt: string | null;
              itemCount: number;
              unresolvedCount: number;
            }>;
          },
      ),

  // How many unresolved issue items does this file have? Shown as a
  // warning on the Mark-as-Uploaded prompt; doesn't block.
  getMassPaymentsFileUnresolvedItems: (fileId: string) =>
    client
      .get(`/mass-payments/files/${fileId}/unresolved-items`)
      .then((r) => r.data as { unresolvedCount: number }),

  // ── Issue reports page ─────────────────────────────────────
  listAllMassPaymentsIssueReports: (sfAccountId?: string) =>
    client
      .get(`/mass-payments/issue-reports`, {
        params: sfAccountId ? { sf_account_id: sfAccountId } : undefined,
      })
      .then((r) => r.data as {
        reports: Array<{
          id: string;
          name: string;
          sfAccountId: string;
          notionalDate: string | null;
          createdAt: string | null;
          closedAt: string | null;
          createdByUsername: string | null;
          closedByUsername: string | null;
          itemCount: number;
          unresolvedCount: number;
          sfUpdatedCount: number;
        }>;
      }),

  getMassPaymentsIssueReport: (reportId: string) =>
    client
      .get(`/mass-payments/issue-reports/${reportId}`)
      .then((r) => r.data as {
        report: {
          id: string;
          name: string;
          sfAccountId: string;
          notionalDate: string | null;
          createdAt: string | null;
          closedAt: string | null;
          createdByUsername: string | null;
          closedByUsername: string | null;
          itemCount: number;
          unresolvedCount: number;
          sfUpdatedCount: number;
          items: Array<{
            id: string;
            reportId: string;
            sourceFileId: string;
            sourceFileName: string | null;
            sourceFileBankKind: string | null;
            paymentIndex: number;
            category: string;
            categoryNote: string | null;
            originalBeneficiaryName: string | null;
            originalAccountNumber: string | null;
            originalSortCode: string | null;
            originalBankName: string | null;
            sfBeneficiaryId: string | null;
            correctedAccountNumber: string | null;
            correctedSortCode: string | null;
            correctedBankName: string | null;
            resolvedAt: string | null;
            sfUpdatedAt: string | null;
            orphanedAt: string | null;
            orphanedToOrphanId: string | null;
            createdAt: string | null;
          }>;
        };
      }),

  previewMassPaymentsCorrections: (reportId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return client
      .post(`/mass-payments/issue-reports/${reportId}/corrections/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data as {
        rowsParsed: number;
        matched: Array<{
          correction: CorrectionRow;
          itemId: string;
        }>;
        unmatched: Array<CorrectionRow & { reason: string }>;
        ambiguous: Array<{
          correction: CorrectionRow;
          candidateItemIds: string[];
          reason: string;
        }>;
      });
  },

  applyMassPaymentsCorrections: (
    reportId: string,
    matches: Array<{ correction: CorrectionRow; itemId: string }>,
  ) =>
    client
      .post(`/mass-payments/issue-reports/${reportId}/corrections/apply`, { matches })
      .then((r) => r.data as { appliedCount: number; filesUpdated: number }),

  manuallyPlaceMassPaymentsCorrection: (
    reportId: string,
    body: {
      correction: CorrectionRow;
      targetFileId: string;
      targetPaymentIndex: number;
    },
  ) =>
    client
      .post(`/mass-payments/issue-reports/${reportId}/corrections/manual`, body)
      .then((r) => r.data as { itemId: string }),

  pushMassPaymentsCorrectionsToSf: (reportId: string) =>
    client
      .post(`/mass-payments/issue-reports/${reportId}/push-to-sf`)
      .then((r) => r.data as {
        pushedCount: number;
        skippedCount: number;
        errors: Array<{ itemId: string | null; error: string }>;
      }),

  setMassPaymentsIssueReportClosed: (reportId: string, closed: boolean) =>
    client
      .post(`/mass-payments/issue-reports/${reportId}/closed`, { closed })
      .then((r) => r.data as { report: any }),

  // ── Orphan payments ──────────────────────────────────────────
  listMassPaymentsOrphans: (params: {
    sfAccountId?: string;
    includeConsumed?: boolean;
  } = {}) =>
    client
      .get("/mass-payments/orphans", {
        params: {
          sf_account_id: params.sfAccountId,
          include_consumed: params.includeConsumed,
        },
      })
      .then((r) => r.data as { orphans: OrphanRow[] }),

  getMassPaymentsOrphan: (orphanId: string) =>
    client
      .get(`/mass-payments/orphans/${orphanId}`)
      .then((r) => r.data as { orphan: OrphanRow }),

  editMassPaymentsOrphan: (orphanId: string, body: {
    parsedRow?: Record<string, any>;
    staffNote?: string;
  }) =>
    client
      .patch(`/mass-payments/orphans/${orphanId}`, body)
      .then((r) => r.data as { orphan: OrphanRow }),

  deleteMassPaymentsOrphan: (orphanId: string) =>
    client
      .delete(`/mass-payments/orphans/${orphanId}`)
      .then((r) => r.data as { deleted: boolean }),

  pushMassPaymentsOrphanToSf: (orphanId: string) =>
    client
      .post(`/mass-payments/orphans/${orphanId}/push-to-sf`)
      .then((r) => r.data as { orphan: OrphanRow }),

  // Chase tracking: flag/unflag orphans as "client has been emailed".
  // Bulk by design — the single-row toggle just sends one id.
  markMassPaymentsOrphansEmailed: (body: {
    orphanIds: string[];
    emailed: boolean;
  }) =>
    client
      .post(`/mass-payments/orphans/mark-emailed`, body)
      .then((r) => r.data as { updated: number }),

  generateMassPaymentsBankFileFromOrphans: (body: {
    orphanIds: string[];
    paymentDate: string;  // YYYY-MM-DD
  }) =>
    client
      .post(`/mass-payments/orphans/generate-bank-file`, body)
      .then((r) => r.data as {
        fileId: string;
        status: string;
        paymentCount: number;
        totalAmountGbp: number;
        bankKind: "ifast" | "freemarket";
        filename: string;
      }),

  // Detach a payment row from a file directly.
  orphanFileRow: (fileId: string, body: {
    paymentIndex: number;
    originIssueItemId?: string;
  }) =>
    client
      .post(`/mass-payments/files/${fileId}/orphan-row`, body)
      .then((r) => r.data as { orphan: OrphanRow }),

  // Orphan a row referenced by an issue item — server resolves the
  // (file, payment_index) from the item.
  orphanFromIssueItem: (itemId: string) =>
    client
      .post(`/mass-payments/issue-items/${itemId}/orphan`)
      .then((r) => r.data as { orphan: OrphanRow }),

  // ── File inline payment-row edit + regenerate stored CSV ─────
  editMassPaymentsPaymentRow: (
    fileId: string,
    paymentIndex: number,
    body: { parsedRow: Record<string, any> },
  ) =>
    client
      .patch(`/mass-payments/files/${fileId}/payments/${paymentIndex}`, body)
      .then((r) => r.data as {
        fileId: string;
        paymentIndex: number;
        regenerated: boolean;
      }),

  // ── Staff notes + compliance flag on files ───────────────────
  setMassPaymentsFileNotes: (fileId: string, notes: string | null) =>
    client
      .post(`/mass-payments/files/${fileId}/notes`, { notes })
      .then((r) => r.data as { fileId: string; staffNotes: string | null }),

  setMassPaymentsFileComplianceFlag: (
    fileId: string,
    body: { flagged: boolean; note?: string | null },
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/compliance-flag`, body)
      .then((r) => r.data as { fileId: string; complianceFlagged: boolean; note: string | null }),

  // Manually re-run bank file generation for an existing file. Used
  // when an orphan-bundle file ended up with zero stored CSVs.
  regenerateMassPaymentsBankFile: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/regenerate-bank-file`)
      .then((r) => r.data as { fileId: string; bankFileCount: number }),

  // Undo an orphan-bundle file: release the orphans back to active +
  // soft-delete the file. Only available on orphan-generated files in
  // non-terminal status.
  releaseMassPaymentsOrphansFromFile: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/release-orphans`)
      .then((r) => r.data as { fileId: string; orphansReleased: number; deleted: boolean }),

  // Export URLs — these return zip streams. Use them as <a href> rather
  // than via axios so the browser handles the download natively. We
  // still pass auth via the standard interceptor by building a full URL
  // that includes the bearer token? — no, the existing /bank-files.zip
  // and /all.zip URLs are served with the same pattern: the FastAPI
  // dependency reads the Authorization header, so we have to fetch
  // these via axios + create a blob URL on the client. See the file
  // download helper below.

  // Stage 4: actually push the file's Payment/Transaction records to SF.
  // For Freemarket the beneficiaryId is chosen at this step (not earlier).
  postMassPaymentsPushToSf: (
    fileId: string,
    body: { beneficiaryId?: string; excludedIndexes?: number[] } = {},
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/push-to-sf`, body)
      .then((r) => r.data as { fileId: string; status: string }),

  // Admin-only reversal: cancel every Payment the file pushed, delete their
  // debit transactions, and move the file back to 'sent_to_bank'.
  postMassPaymentsPullBack: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/pull-back`)
      .then((r) => r.data as {
        fileId: string;
        status: string;
        paymentsCancelled: number;
        transactionsDeleted: number;
      }),

  getMassPushDuplicates: (params: { accountId?: string; scope: "client" | "all" }) =>
    client.get("/mass-payments/mass-push/duplicates", { params }).then((r) => r.data as {
      files: Array<{
        fileId: string;
        filename: string;
        accountName: string;
        bankKind: "ifast" | "freemarket";
        payableCount: number;
        existingExcludedIndexes: number[];
      }>;
      duplicateGroups: Array<{
        name: string;
        amount: number;
        count: number;
        payments: Array<{
          fileId: string;
          filename: string;
          accountName: string;
          bankKind: "ifast" | "freemarket";
          index: number;
          name: string;
          amount: number | null;
          reference: string;
          sortCode: string;
          accountNumber: string;
          isOrphan: boolean;
          originalFile: string | null;
        }>;
      }>;
      totalFiles: number;
      totalPayablePayments: number;
      duplicatePaymentCount: number;
    }),

  // Update which payment indexes are excluded (bounce-backs etc.).
  // Replaces the existing list. Valid only at sent_to_bank or paid_not_pushed.
  postMassPaymentsUpdateExclusions: (
    fileId: string,
    excludedIndexes: number[],
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/update-exclusions`, {
        excludedIndexes,
      })
      .then((r) => r.data as { fileId: string; excludedIndexes: number[] }),

  // Paid-files report — JSON preview. Use buildMassPaymentsReportUrl for
  // CSV/PDF downloads since those need to go through the browser's auth
  // header on a real <a download> click (handled by downloadAuthedFile).
  getMassPaymentsReportPreview: (
    accountId: string,
    from: string,
    to: string,
    accountName?: string,
  ) =>
    client
      .get("/mass-payments/report", {
        params: { accountId, from, to, format: "json", accountName },
      })
      .then((r) => r.data as {
        accountId: string;
        accountName: string;
        dateFrom: string;
        dateTo: string;
        files: Array<{
          fileId: string;
          filename: string;
          paymentDate: string | null;
          bankKind: "ifast" | "freemarket";
          paymentCountParsed: number;
          totalAmountParsedGbp: number;
          excludedCount: number;
          paymentCountPushed: number;
          totalAmountPushedGbp: number;
          pushedToSfAt: string | null;
        }>;
        totals: {
          totalFiles: number;
          parsedCount: number;
          parsedSumGbp: number;
          pushedCount: number;
          pushedSumGbp: number;
        };
        generatedAt: string;
      }),

  // Soft-delete a file (hidden from the list, recoverable in Supabase /
  // via the admin restore endpoint).
  deleteMassPaymentsFile: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/delete`)
      .then((r) => r.data as { fileId: string; isDeleted: boolean; alreadyDeleted?: boolean }),

  // Restore a soft-deleted file (admin-only).
  restoreMassPaymentsFile: (fileId: string) =>
    client
      .post(`/mass-payments/files/${fileId}/restore`)
      .then((r) => r.data as { fileId: string; isDeleted: boolean }),

  // iFAST mark-as-paid. Accepts additional excludedIndexes chosen by
  // staff in the mark-as-paid modal's search-and-exclude.
  postMassPaymentsMarkAsPaid: (
    fileId: string,
    body?: { excludedIndexes?: number[] },
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/mark-as-paid`, body ?? {}, {
        timeout: 60_000,
      })
      .then(
        (r) =>
          r.data as {
            fileId: string;
            status: string;
          },
      ),

  // Freemarket: find the closest-name-match Beneficiary__c for the
  // mark-as-paid autocomplete. Returns the auto-pick + full list.
  getMassPaymentsClosestBenefMatch: (fileId: string) =>
    client
      .get(`/mass-payments/files/${fileId}/closest-benef-match`)
      .then(
        (r) =>
          r.data as {
            fileId: string;
            targetName: string;
            topMatch: { sfId: string; name: string; score: number } | null;
            allBenefs: Array<{ sfId: string; name: string; score: number }>;
          },
      ),

  // Freemarket mark-as-paid (lifecycle stage 3). Under the new flow this
  // is a status flip only — beneficiary selection and SF push happen at
  // the separate /push-to-sf step. Body is optional and may carry just
  // extra exclusions if any.
  postMassPaymentsMarkAsPaidFreemarket: (
    fileId: string,
    body?: { excludedIndexes?: number[] },
  ) =>
    client
      .post(`/mass-payments/files/${fileId}/mark-as-paid-freemarket`, body ?? {}, {
        timeout: 60_000,
      })
      .then(
        (r) =>
          r.data as {
            fileId: string;
            status: string;
          },
      ),

  // Download an authenticated zip endpoint. Anchor tags can't carry the
  // Bearer token, so we fetch the blob via axios then trigger a download
  // from an object URL. Used for the "Download bank files" (zip of all
  // bank CSVs) and "Download .zip" (bank CSVs + original) buttons.
  downloadAuthedZip: async (
    relativeUrl: string,
    suggestedFilename: string,
  ): Promise<void> => {
    // relativeUrl is API-relative (starts with "/api/v1/..."); strip the
    // "/api/v1" prefix since the axios client already has that baseURL.
    const path = relativeUrl.replace(/^\/api\/v1/, "");
    const resp = await client.get(path, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  // Download an authenticated file endpoint, letting the SERVER name the
  // file. Same blob-via-axios trick as downloadAuthedZip, but reads the
  // filename out of Content-Disposition (the API exposes that header via
  // CORS) instead of taking it from the caller. Used by the mass-payments
  // "Download API File" button, where the filename carries the bank
  // file's timestamp and so can't be known up front.
  downloadAuthedFile: async (
    relativeUrl: string,
    fallbackFilename: string,
  ): Promise<void> => {
    const path = relativeUrl.replace(/^\/api\/v1/, "");
    const resp = await client.get(path, { responseType: "blob" });

    let filename = fallbackFilename;
    const disposition = resp.headers?.["content-disposition"];
    if (typeof disposition === "string") {
      // Handles filename="x.csv" and RFC 5987 filename*=UTF-8''x.csv
      const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const plain = /filename="?([^";]+)"?/i.exec(disposition);
      if (star?.[1]) {
        try {
          filename = decodeURIComponent(star[1]);
        } catch {
          filename = star[1];
        }
      } else if (plain?.[1]) {
        filename = plain[1];
      }
    }

    const blobUrl = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  // ── Admin KPIs ─────────────────────────────
  getKpisTarget: () =>
    client.get("/admin/kpis/target").then((r) => r.data as { annualTargetGbp: number }),

  patchKpisTarget: (annualTargetGbp: number) =>
    client
      .patch("/admin/kpis/target", { annualTargetGbp })
      .then((r) => r.data as { annualTargetGbp: number }),

  getKpisClientAcquisition: (params: {
    outlierSd?: number;
    groupBy?: "pm" | "industry" | "vertical" | null;
    excludeIndustries?: string[];
    excludeVerticals?: string[];
  } = {}) =>
    client.get("/admin/kpis/client-acquisition", {
      timeout: 180_000,
      params: {
        ...(params.outlierSd != null ? { outlier_sd: params.outlierSd } : {}),
        ...(params.groupBy ? { group_by: params.groupBy } : {}),
        ...(params.excludeIndustries?.length ? { exclude_industries: params.excludeIndustries.join(",") } : {}),
        ...(params.excludeVerticals?.length ? { exclude_verticals: params.excludeVerticals.join(",") } : {}),
      },
    }).then((r) => r.data as {
      active: {
        activeCount: number; active25kCount: number;
        targetClients: number; revenueFloor: number;
        progressPct: number; progress25kPct: number;
        clients: Array<{
          accountId: string; accountName: string; vertical: string | null;
          traderName: string | null; t12Revenue: number; meetsFloor: boolean;
        }>;
      };
      concentration: {
        totalRevenue: number; clientCount: number; hhi: number;
        topShares: Record<string, number>;
        buckets: Array<{ band: string; clientCount: number; revenue: number; revenuePct: number }>;
        topClients: Array<{ accountId: string; accountName: string; revenue: number; sharePct: number }>;
        cumulative: Array<{ rank: number; rankPct: number; cumRevenuePct: number }>;
        windowStart: string; windowEnd: string; revenueFloor: number; clientsAboveFloor: number;
      };
      lifecycle: {
        clients: Array<{ accountId: string; accountName: string; status: string | null; series: number[]; peakVolume: number }>;
        averageCurve: Array<{ month: number; avgVolume: number; clientsAtMonth: number }>;
        averageCurveExOutliers: Array<{ month: number; avgVolume: number; clientsAtMonth: number }>;
        outlierSd: number; outlierCount: number;
        outlierClients: Array<{ accountName: string; peakVolume: number }>;
        stats: Record<string, number | null>;
        smoothing: number; clientCount: number;
        groupBy: string | null;
        groups: Array<{ name: string; clientCount: number; curve: Array<{ month: number; avgVolume: number; clientsAtMonth: number }> }>;
        dimensions: { pms: string[]; industries: string[]; verticals: string[] };
      };
      churn: {
        headcount: {
          eligibleCount: number; retainedCount: number; churnedCount: number;
          retentionRatePct: number | null; churnRatePct: number | null; minPriorTrades: number;
          churnedClients: Array<{
            accountId: string; accountName: string | null; priorYearTrades: number;
            priorYearRevenue: number; lifetimeRevenue: number; monthsTraded: number;
            annualisedRevenueLost: number; lastTradeDate: string | null;
          }>;
        };
        revenue: {
          priorPeriodRevenue: number; revenueLostAnnualised: number;
          revenueChurnPct: number | null; revenueRetainedPct: number | null;
        };
        riskScore: {
          averageScore: number | null; scoredClients: number;
          distribution: Record<string, number>;
          weights: { frequency: number; volume: number; recency: number };
          recentWindowDays: number;
          clients: Array<{
            accountId: string; accountName: string | null; score: number;
            freqScore: number; volScore: number; recencyScore: number;
            tradesLast90: number; lifetimeTrades: number; daysSinceLastTrade: number;
          }>;
        };
        windows: { today: string; lastYearStart: string; priorYearStart: string; priorYearEnd: string };
        avoidable: {
          avoidableCount: number; churnedConsidered: number; avoidablePct: number | null;
          monthsBefore: number; monthsAfter: number;
          avoidableClients: Array<{
            accountId: string; accountName: string | null; priorYearTrades: number;
            annualisedRevenueLost: number; lastTradeDate: string | null; commsInWindow: number;
            closedForCompliance: boolean;
          }>;
        };
      };
      loadedAt: string | null;
    }),

  getKpisActivity: (params: { days?: number; excludeUserIds?: string[] } = {}) =>
    client.get("/admin/kpis/activity", {
      timeout: 180_000,
      params: {
        ...(params.days != null ? { days: params.days } : {}),
        ...(params.excludeUserIds && params.excludeUserIds.length
          ? { exclude_user_ids: params.excludeUserIds.join(",") }
          : {}),
      },
    }).then((r) => r.data as {
      windowDays: number;
      generatedAt: string;
      totals: {
        activeUsers: number; logins: number; recordsCreated: number;
        recordsModified: number; objectsTracked: number;
      };
      byUser: Array<{
        userId: string; name: string; isActive: boolean;
        logins: number; created: number; modified: number;
        lastLogin: string | null; lastActiveDay: string | null;
      }>;
      byObject: Array<{ object: string; created: number; modified: number }>;
      timeline: Array<{ date: string; created: number; modified: number; logins: number }>;
      loginsByHour: Array<{ hour: number; logins: number }>;
      loginTypes: Array<{ type: string; count: number }>;
    }),

  getKpisActivityUser: (userId: string, params: { days?: number } = {}) =>
    client.get(`/admin/kpis/activity/user/${encodeURIComponent(userId)}`, {
      timeout: 180_000,
      params: { ...(params.days != null ? { days: params.days } : {}) },
    }).then((r) => r.data as {
      userId: string;
      name: string;
      isActive: boolean;
      windowDays: number;
      generatedAt: string;
      loginsTruncated: boolean;
      totals: {
        logins: number; recordsCreated: number;
        recordsModified: number; objectsTracked: number;
      };
      logins: Array<{
        time: string; type: string; status: string | null;
        application: string | null; browser: string | null;
        platform: string | null; sourceIp: string | null;
      }>;
      byObject: Array<{ object: string; created: number; modified: number }>;
      byDay: Array<{
        date: string; created: number; modified: number; logins: number;
        objects: Array<{ object: string; created: number; modified: number }>;
      }>;
    }),

  getKpisActivityAccountChanges: (params: { days?: number; userId?: string; excludeUserIds?: string[] } = {}) =>
    client.get("/admin/kpis/activity/account-changes", {
      timeout: 180_000,
      params: {
        ...(params.days != null ? { days: params.days } : {}),
        ...(params.userId ? { user_id: params.userId } : {}),
        ...(params.excludeUserIds && params.excludeUserIds.length
          ? { exclude_user_ids: params.excludeUserIds.join(",") }
          : {}),
      },
    }).then((r) => r.data as {
      windowDays: number;
      generatedAt: string;
      userScoped: boolean;
      trackingActive: boolean;
      accountsModified: number;
      historyEvents: number;
      byField: Array<{ field: string; label: string; changes: number; users: number }>;
      byUser: Array<{ userId: string; name: string; changes: number }>;
      samples: Array<{
        field: string; label: string; oldValue: string | null; newValue: string | null;
        accountId: string | null; accountName: string | null;
        userId: string | null; userName: string; at: string;
      }>;
      samplesTruncated: boolean;
    }),

  getKpisExecutiveOverview: () =>
    client.get("/admin/kpis/executive-overview", { timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      annualTargetGbp: number;
      kpis: {
        ytdRevenue: number;
        ytdRevenuePriorYear: number;
        /** Salesforce trades only: no iFAST interest, no revenue adjustments. */
        ytdTradeRevenue?: number;
        ytdTradeRevenuePriorYear?: number;
        tradeYoyPct?: number;
        /** ytdRevenue − ytdTradeRevenue. */
        ytdInterestAndAdjustments?: number;
        yoyPct: number;
        targetYtd: number;
        varianceToTarget: number;
        variancePct: number;
        runRateFullYear: number;
        runRateVsTargetPct: number;
        activeClients: number;
      };
      monthly: Array<{
        month: string;
        monthLabel: string;
        revenue: number;
        target: number;
        forecastConservative: number;
        forecastModerate: number;
        forecastOptimistic: number;
        isPast: boolean;
        hitTarget: boolean;
      }>;
      forecasts: {
        pyFullRev: number;
        impliedGrowthPct: number;
        actualYoyPct: number;
        outperformancePp: number;
        conservativeAnnual: number;
        moderateAnnual: number;
        optimisticAnnual: number;
      };
      byVertical: Array<{ vertical: string; revenue: number }>;
      byTradeType: Array<{ tradeType: string; revenue: number }>;
      topByRevenue: Array<{ accountId: string; accountName: string; vertical: string; value: number; pctOfTotal: number }>;
      topByNetProfit: Array<{ accountId: string; accountName: string; vertical: string; value: number; pctOfTotal: number }>;
      revenueConcentrationPct: number;
      netProfitConcentrationPct: number;
      concentrationHistory: Array<{
        quarterLabel: string;
        quarterStart: string;
        isCurrent: boolean;
        topRevSharePct: number;
        topProfitSharePct: number;
        totalRevenueGbp: number;
        totalProfitGbp: number;
      }>;
      ytdNetProfit: number;
    }),

  /** Trade profit per period for the hub profit widget. */
  getKpisProfitSeries: ({ refresh, ...params }: { basis: "dealt" | "settled"; period: "day" | "week" | "month" | "quarter" | "year"; count: number; refresh?: boolean }) =>
    client.get("/admin/kpis/profit-series", { params: refresh ? { ...params, refresh: 1 } : params, timeout: 180_000 }).then((r) => r.data as {
      basis: "dealt" | "settled"; period: string; count: number;
      warning?: string | null; missingFields?: string[]; types?: string[];
      points: Array<{ start: string; end: string; label: string; profit: number; trades: number; current: boolean;
        split?: Record<string, { profit: number; trades: number }> }>;
      current: { start: string; end: string; label: string; profit: number; trades: number; current: boolean;
        split?: Record<string, { profit: number; trades: number }> } | null;
      previous: { label: string; profit: number; profitToDate: number };
      loadedAt: string | null;
    }),
  /** The trades behind one bar of the hub profit widget (report page). */
  getKpisProfitTrades: (params: { basis: "dealt" | "settled"; period: string; start: string; type?: string }) =>
    client.get("/admin/kpis/profit-trades", { params, timeout: 180_000 }).then((r) => r.data as {
      basis: "dealt" | "settled"; period: string; label: string; start: string; end: string;
      type: string | null; types: string[]; total: number; count: number; truncated: boolean;
      rows: Array<{
        id: string | null; name: string | null; date: string | null; type: string | null;
        sellCurrency: string | null; buyCurrency: string | null; buyAmountGbp: number; valueDate: string | null;
        accountId: string | null; client: string | null; trader: string | null; profitGbp: number;
      }>;
      loadedAt: string | null;
    }),
  getReportingConversations: () =>
    client.get("/reporting/conversations").then((r) => r.data as { conversations: ReportingConversationSummary[]; shared: ReportingConversationSummary[] }),
  getReportingConversation: (id: string) =>
    client.get(`/reporting/conversations/${encodeURIComponent(id)}`).then((r) => r.data as {
      id: string; title: string; mine: boolean; shared: boolean; ownerName: string | null; turns: ReportingTurn[];
    }),
  updateReportingConversation: (id: string, body: { shared: boolean }) =>
    client.patch(`/reporting/conversations/${encodeURIComponent(id)}`, body).then((r) => r.data as { id: string; shared: boolean }),
  deleteReportingConversation: (id: string) =>
    client.delete(`/reporting/conversations/${encodeURIComponent(id)}`).then((r) => r.data as { ok: boolean }),
  askReporting: (body: { conversationId?: string | null; message: string; templateId?: string | null }) =>
    client.post("/reporting/ask", body, { timeout: 180_000 }).then((r) => r.data as {
      conversationId: string; turn: ReportingTurn; result: ReportingResult | null;
    }),
  runReportingTurn: (turnId: string) =>
    client.post(`/reporting/turns/${encodeURIComponent(turnId)}/run`, null, { timeout: 180_000 }).then((r) => r.data as { result: ReportingResult; stepInfo: ReportingStepInfo[] }),
  getReportingTemplates: () =>
    client.get("/reporting/templates").then((r) => r.data as { templates: ReportingTemplate[] }),
  saveReportingTemplate: (body: { turnId: string; name: string }) =>
    client.post("/reporting/templates", body).then((r) => r.data as ReportingTemplate),
  updateReportingTemplate: (id: string, body: { name?: string; shared?: boolean }) =>
    client.patch(`/reporting/templates/${encodeURIComponent(id)}`, body).then((r) => r.data as ReportingTemplate),
  deleteReportingTemplate: (id: string) =>
    client.delete(`/reporting/templates/${encodeURIComponent(id)}`).then((r) => r.data as { ok: boolean }),
  createReportingWidget: (body: { turnId?: string; templateId?: string; title: string; refreshMinutes: number | null; view: ReportingWidgetView }) =>
    client.post("/reporting/widgets", body).then((r) => r.data as ReportingWidget),
  /** refresh "auto": run it if it's due; "force": run it now (at most once a minute). */
  getReportingWidget: (id: string, refresh?: "auto" | "force") =>
    client.get(`/reporting/widgets/${encodeURIComponent(id)}`, { params: refresh ? { refresh } : undefined, timeout: 180_000 })
      .then((r) => r.data as ReportingWidget),
  updateReportingWidget: (id: string, body: { title?: string; refreshMinutes?: number | null }) =>
    client.patch(`/reporting/widgets/${encodeURIComponent(id)}`, body).then((r) => r.data as ReportingWidget),
  runReportingTemplate: (id: string) =>
    client.post(`/reporting/templates/${encodeURIComponent(id)}/run`, null, { timeout: 180_000 }).then((r) => r.data as {
      result: ReportingResult; stepInfo: ReportingStepInfo[]; soqlRun: string | null;
    }),
  getReportingSchema: (refresh?: boolean) =>
    client.get("/reporting/schema", { params: refresh ? { refresh: 1 } : undefined, timeout: 60_000 }).then((r) => r.data as ReportingSchemaView),
  getKpisRevenueTrends: () =>
    client.get("/admin/kpis/revenue-trends", { timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      yoyMonthly: Array<{ month: string; monthLabel: string; currentYear: number; priorYear: number }>;
      periodA: { startDate: string; endDate: string; revenue: number; volume: number; tradeCount: number; realisedMargin: number };
      periodB: { startDate: string; endDate: string; revenue: number; volume: number; tradeCount: number; realisedMargin: number };
      verticalMix: Array<Record<string, string | number>>;
      verticals: string[];
      topGainers: Array<{ accountId: string; accountName: string; currentYearRevenue: number; priorYearRevenue: number; changeGbp: number; changePct: number }>;
      topDecliners: Array<{ accountId: string; accountName: string; currentYearRevenue: number; priorYearRevenue: number; changeGbp: number; changePct: number }>;
    }),

  getKpisClientPerformance: (params: { vertical?: string; trader?: string; flag?: string; accountId?: string } = {}) =>
    client.get("/admin/kpis/client-performance", { params, timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      annualTargetGbp: number;
      active: Record<string, any>;
      pipeline: Record<string, any>;
      clients: Array<{ accountId: string; accountName: string; vertical: string; traderName: string | null; flag: string; cyYtdRevenue: number; pyYtdRevenue: number; yoyChangeGbp: number; runRateFy: number }>;
      selectedClient: any | null;
      filters: { verticals: string[]; traders: string[]; flags: string[]; applied: { vertical: string; trader: string; flag: string } };
    }),

  getKpisTeamPerformance: (params: { role?: "trader" | "consultant"; personId?: string } = {}) =>
    client.get("/admin/kpis/team-performance", { params, timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      role: "trader" | "consultant";
      leaderboard: Array<{
        personId: string;
        personName: string;
        clientCount: number;
        atRiskClientCount: number;
        cyYtdRevenue: number;
        cyYtdVolume: number;
        cyYtdTradeCount: number;
        cyYtdMarginPct: number;
        pyYtdRevenue: number;
        pyYtdVolume: number;
        yoyChangeGbp: number;
        yoyDisplay: string;
        yoyPositive: boolean | null;
        runRateFy: number;
        netContribution: number;
        netContributionPct: number;
        biggestClientName: string;
        biggestClientRevenue: number;
      }>;
      selectedPerson: any | null;
    }),

  refreshKpis: () => client.post("/admin/kpis/refresh", null, { timeout: 300_000 }).then((r) => r.data as { refreshed: boolean } & KpisStatus),
  getKpisStatus: () => client.get("/admin/kpis/status").then((r) => r.data as KpisStatus),

  getKpisMonthDrilldown: (month: string, top: number = 10) =>
    client.get("/admin/kpis/month-drilldown", { params: { month, top }, timeout: 180_000 }).then((r) => r.data as {
      month: string;
      loadedAt: string;
      totalRevenue: number;
      tradeCount: number;
      clientCount: number;
      topClients: Array<{ accountId: string; accountName: string; vertical: string; revenue: number; tradeCount: number; pctOfMonth: number }>;
      other: {
        revenue: number;
        clientCount: number;
        pctOfMonth: number;
        clients: Array<{ accountId: string; accountName: string; vertical: string; revenue: number; tradeCount: number }>;
      };
    }),

  getKpisFeeMatrix: () =>
    client.get("/admin/kpis/fee-matrix").then((r) => r.data as {
      matrix: Record<string, Record<string, number>>;
      banks: Array<{ slug: string; label: string }>;
      rails: string[];
      settlementsCostPerMin: number;
      settlementsSlaMinutes: number;
      tradeLinkedPaymentExtraMinutes: number;
      tradeNoBenefMinutes: number;
      bankPaymentMinutes: number;
      includeSettlementsCost: boolean;
      avoidableWindowMonthsBefore: number;
      avoidableWindowMonthsAfter: number;
    }),

  setAvoidableExclusion: (accountId: string, closedForCompliance: boolean) =>
    client.post("/admin/kpis/avoidable-exclusions", { accountId, closedForCompliance })
      .then((r) => r.data as { accountIds: string[] }),

  patchKpisFeeMatrix: (body: {
    matrix?: Record<string, Record<string, number>>;
    settlementsCostPerMin?: number;
    settlementsSlaMinutes?: number;
    tradeLinkedPaymentExtraMinutes?: number;
    tradeNoBenefMinutes?: number;
    bankPaymentMinutes?: number;
    includeSettlementsCost?: boolean;
    avoidableWindowMonthsBefore?: number;
    avoidableWindowMonthsAfter?: number;
  }) =>
    client.patch("/admin/kpis/fee-matrix", body).then((r) => r.data as {
      matrix: Record<string, Record<string, number>>;
      banks: Array<{ slug: string; label: string }>;
      rails: string[];
      settlementsCostPerMin: number;
      settlementsSlaMinutes: number;
      tradeLinkedPaymentExtraMinutes: number;
      tradeNoBenefMinutes: number;
      bankPaymentMinutes: number;
      includeSettlementsCost: boolean;
      avoidableWindowMonthsBefore: number;
      avoidableWindowMonthsAfter: number;
    }),

  // ── Revenue adjustments (Equals etc) ───────────
  // ── iFAST Interest Earning ─────────────────────
  // ── Team Performance: trader period comparison ──
  getKpisPeriodComparison: (p: {
    traderId: string; period: "quarter" | "half"; index: number;
    year: number; excludePayroll: boolean;
  }) =>
    client.get("/admin/kpis/team/period-comparison", {
      params: p, timeout: 120_000,
    }).then((r) => r.data as PeriodComparison),

  /** Returns a blob — the caller is responsible for triggering the download. */
  getKpisPeriodComparisonPdf: (p: {
    traderId: string; period: "quarter" | "half"; index: number;
    year: number; excludePayroll: boolean;
  }) =>
    client.get("/admin/kpis/team/period-comparison.pdf", {
      params: p, responseType: "blob", timeout: 120_000,
    }).then((r) => r.data as Blob),

  getKpisInterest: (month?: string) =>
    client.get("/admin/kpis/interest", { params: month ? { month } : {} })
      .then((r) => r.data as InterestMonth),

  getKpisInterestLive: () =>
    client.get("/admin/kpis/interest/live").then((r) => r.data as {
      pots: InterestLivePot[];
      asOf: string;
    }),

  postKpisInterestSnapshot: (body: {
    date?: string; currencies?: string[]; overwrite?: boolean;
  } = {}) =>
    client.post("/admin/kpis/interest/snapshot", body).then((r) => r.data as {
      date: string;
      written: Array<{ currency: string; pot: string; balance: number; interest: number }>;
      skipped: string[];
    }),

  putKpisInterestRates: (rateCard: Record<string, InterestTier[]>) =>
    client.put("/admin/kpis/interest/rates", { rateCard }).then((r) => r.data as {
      rateCard: Record<string, InterestTier[]>;
    }),

  putKpisInterestCutoffs: (cutoffHours: Record<string, number>) =>
    client.put("/admin/kpis/interest/cutoffs", { cutoffHours }).then((r) => r.data as {
      cutoffHours: Record<string, number>;
    }),

  getKpisInterestDiagnostics: () =>
    client.get("/admin/kpis/interest/diagnostics").then((r) => r.data as {
      accounts: Array<{
        bankAccountId: string;
        accountId: string | null;
        accountName: string | null;
        currency: string;
        pot: "viban" | "pooled";
        balance: number;
        txCount: number;
      }>;
      potTotals: Record<string, number>;
      accountsWithNoTransactions: number;
    }),

  postKpisInterestBackfill: (body: { start: string; end?: string; overwrite?: boolean }) =>
    client.post("/admin/kpis/interest/backfill", body, { timeout: 300_000 })
      .then((r) => r.data as {
        start: string; end: string; written: number; skipped: number;
      }),

  postKpisInterestRecompute: (month: string) =>
    client.post("/admin/kpis/interest/recompute", { month }).then((r) => r.data as {
      month: string; updated: number;
    }),

  putKpisInterestActual: (body: {
    month: string; currency: string;
    amountCcy: number; amountGbp: number; notes?: string;
  }) => client.put("/admin/kpis/interest/actuals", body).then((r) => r.data as { id: string }),

  deleteKpisInterestActual: (actualId: string) =>
    client.delete(`/admin/kpis/interest/actuals/${actualId}`).then((r) => r.data as { deleted: boolean }),

  getKpisRevenueAdjustments: () =>
    client.get("/admin/kpis/revenue-adjustments").then((r) => r.data as {
      adjustments: Array<{
        id: string;
        month: string;         // YYYY-MM-DD (1st of month)
        label: string;
        amountGbp: number;
        notes: string | null;
        createdAt: string | null;
        updatedAt: string | null;
      }>;
    }),

  postKpisRevenueAdjustment: (body: {
    month: string;             // YYYY-MM or YYYY-MM-DD
    label: string;
    amountGbp: number;
    notes?: string | null;
  }) =>
    client.post("/admin/kpis/revenue-adjustments", body).then((r) => r.data as { id: string }),

  patchKpisRevenueAdjustment: (id: string, body: {
    month?: string;
    label?: string;
    amountGbp?: number;
    notes?: string | null;
  }) =>
    client.patch(`/admin/kpis/revenue-adjustments/${id}`, body).then((r) => r.data),

  deleteKpisRevenueAdjustment: (id: string) =>
    client.delete(`/admin/kpis/revenue-adjustments/${id}`).then((r) => r.data),

  // ── Forecast (current month, based on last 3 whole months) ───
  getKpisForecast: () =>
    client.get("/admin/kpis/forecast").then((r) => r.data as {
      loadedAt: string;
      windowStart: string;
      windowEnd: string;
      windowLabel: string;
      currentMonthLabel: string;
      today: string;
      daysElapsed: number;
      daysInMonth: number;
      totals: {
        forecastRevenue: number;
        forecastProfit: number;
        mtdRevenue: number;
        mtdProfit: number;
        expectedMtdRevenue: number;
        expectedMtdProfit: number;
        mtdVarianceRevenue: number;
        mtdVarianceProfit: number;
        atRiskForecastRevenue: number;
        atRiskForecastProfit: number;
        notAtRiskForecastRevenue: number;
        notAtRiskForecastProfit: number;
        atRiskForecastRevenuePct: number;
        atRiskForecastProfitPct: number;
        clientCount: number;
        atRiskCount: number;
      };
      rows: Array<{
        accountId: string;
        accountName: string;
        vertical: string;
        traderName: string;
        flag: string;
        atRisk: boolean;
        windowRevenue: number;
        windowProfit: number;
        forecastRevenue: number;
        forecastProfit: number;
        mtdRevenue: number;
        mtdProfit: number;
        expectedMtdRevenue: number;
        expectedMtdProfit: number;
        mtdVarianceRevenue: number;
        mtdVarianceProfit: number;
      }>;
    }),

  getKpisProfitability: (params: {
    vertical?: string;
    trader?: string;
    period?: string;
    periodStart?: string;
    periodEnd?: string;
  } = {}) =>
    client.get("/admin/kpis/profitability", { params, timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      period: { id: string; label: string; start: string; end: string };
      config: {
        includeSettlementsCost: boolean;
        settlementsCostPerMin: number;
        settlementsSlaMinutes: number;
      };
      totals: {
        revenueGbp: number;
        commissionsGbp: number;
        paymentFeesGbp: number;
        settlementsGbp: number;
        totalCostsGbp: number;
        netProfitGbp: number;
        netMarginPct: number;
      };
      byRail: Array<{ rail: string; paymentCount: number; feesGbp: number }>;
      byBank: Array<{ bankSlug: string; bankLabel: string; paymentCount: number; feesGbp: number }>;
      clients: Array<{
        accountId: string;
        accountName: string;
        vertical: string;
        traderName: string | null;
        revenueGbp: number;
        tradeCount: number;
        paymentCount: number;
        commissionsGbp: number;
        paymentFeesGbp: number;
        settlementsGbp: number;
        settlementsExcludedPayroll: boolean;
        totalCostsGbp: number;
        netProfitGbp: number;
        netMarginPct: number;
      }>;
      filters: {
        verticals: string[];
        traders: string[];
        applied: { vertical: string; trader: string };
      };
    }),

  getKpisClientDetail: (accountId: string) =>
    client.get("/admin/kpis/client-detail", { params: { accountId }, timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      account: {
        accountId: string;
        accountName: string;
        vertical: string;
        traderName: string | null;
        consultantName: string | null;
        annualFxExposure: number;
      };
      monthlyRevenue: Array<{ month: string; monthLabel: string; revenue: number; volume: number; tradeCount: number }>;
      qtdComparison: {
        currentLabel: string;
        previousLabel: string;
        current: { revenue: number; volume: number; tradeCount: number };
        previous: { revenue: number; volume: number; tradeCount: number };
      };
      monthlyMargin: Array<{ month: string; monthLabel: string; volumeWeightedPct: number; simpleMeanPct: number }>;
      marginAvailable: boolean;
      walletShare: Array<{ month: string; monthLabel: string; volumeGbp: number; expectedMonthlyGbp: number; sharePct: number }>;
      walletShareSummary: { annualFxExposure: number; ytdVolume: number; pyVolume: number; pyYtdVolume: number; ytdSharePct: number; pySharePct: number; pyYtdSharePct: number; expectedYtdSharePct: number };
    }),

  getKpisGroupSummaries: () =>
    client.get("/admin/kpis/group-summaries", { timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      includeSettlementsCost: boolean;
      byVertical: Array<{
        dim: string;
        clientCount: number;
        ytdRevenue: number;
        ytdNetProfit: number;
        ytdCommissions: number;
        ytdPaymentFees: number;
        ytdSettlements: number;
        marginPct: number;
        avgRevPerClient: number;
        pyRevenue: number;
        yoyPct: number | null;
      }>;
      byTrader: Array<{
        dim: string;
        clientCount: number;
        ytdRevenue: number;
        ytdNetProfit: number;
        ytdCommissions: number;
        ytdPaymentFees: number;
        ytdSettlements: number;
        marginPct: number;
        avgRevPerClient: number;
        pyRevenue: number;
        yoyPct: number | null;
      }>;
      byIndustry: Array<{
        dim: string;
        clientCount: number;
        ytdRevenue: number;
        ytdNetProfit: number;
        ytdCommissions: number;
        ytdPaymentFees: number;
        ytdSettlements: number;
        marginPct: number;
        avgRevPerClient: number;
        pyRevenue: number;
        yoyPct: number | null;
      }>;
    }),

  getKpisActionPlan: (params: { trader?: string; minDeclinePct?: number } = {}) =>
    client.get("/admin/kpis/action-plan", { params, timeout: 180_000 }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      qtdLabel: string;
      prevQtdLabel: string;
      qtdStart: string;
      qtdEnd: string;
      prevQtdStart: string;
      prevQtdEnd: string;
      filters: { traders: string[]; applied: { trader: string; minDeclinePct: number } };
      accounts: Array<{
        accountId: string;
        accountName: string;
        vertical: string;
        accountStatus: string;
        traderName: string | null;
        qtdRevenue: number;
        prevQtdRevenue: number;
        qtdTrades: number;
        prevQtdTrades: number;
        deltaGbp: number;
        deltaPct: number | null;
        lastTradeDate: string | null;
        daysSinceTrade: number | null;
        annualFxExposure: number;
      }>;
      moneyLeftOnTable: Array<{
        accountId: string;
        accountName: string;
        vertical: string;
        accountStatus: string;
        traderName: string | null;
        annualFxExposure: number;
        ytdVolumeGbp: number;
        uncapturedGbp: number;
        capturedPct: number;
      }>;
      moneyLeftOnTableTotal: number;
      qtdRevenueTotal: number;
      prevQtdRevenueTotal: number;
      deltaTotal: number;
      accountCount: number;
    }),

  getKpisProfitabilityDetail: (
    accountId: string,
    periodParams: { period?: string; periodStart?: string; periodEnd?: string } = {},
  ) =>
    client.get("/admin/kpis/profitability-detail", {
      params: { accountId, ...periodParams },
      timeout: 180_000,
    }).then((r) => r.data as {
      asOf: string;
      loadedAt: string;
      period: { id: string; label: string; start: string; end: string };
      account: {
        accountId: string;
        accountName: string;
        vertical: string;
        traderName: string | null;
        isPayrollVertical: boolean;
      };
      revenue: {
        total: number;
        tradeCount: number;
        byTradeType: Array<{ tradeType: string; revenue: number; tradeCount: number; pctOfRevenue: number }>;
      };
      commissions: {
        total: number;
        byRecipient: Array<{ recipient: string; pct: number; base: number; amount: number }>;
      };
      fees: {
        total: number;
        paymentCount: number;
        byRail: Array<{ rail: string; paymentCount: number; feesGbp: number; avgFeeGbp: number }>;
        byBank: Array<{ bankSlug: string; bankLabel: string; paymentCount: number; feesGbp: number; avgFeeGbp: number }>;
      };
      settlements: {
        perPaymentCost: number;
        perPaymentBreakdown: Array<{ label: string; paymentCount: number; costGbp: number }>;
        noBenefTradeCount: number;
        noBenefTradeOverhead: number;
        bankPaymentCount: number;
        bankPaymentCost: number;
        totalRaw: number;
        total: number;
        includedInTotal: boolean;
        excludedPayroll: boolean;
        costPerMin: number;
        slaMinutes: number;
        tradeLinkedExtraMinutes: number;
        tradeNoBenefMinutes: number;
        bankPaymentMinutes: number;
      };
      totals: {
        totalCosts: number;
        netProfit: number;
        netMarginPct: number;
      };
    }),

  // ── Control Centre (super-admin only) ────────────────────
  getControlCentreCounters: () =>
    client.get("/admin/control-centre/counters").then((r) => r.data as {
      asOf: string;
      users: { total: number; newLast24h: number; onlineNow: number; activeSessions: number };
      invites: { pending: number; registeredLast24h: number };
      massPayments: {
        uploadedToday: number; inProgress: number; readyToPay: number;
        sentToBank: number; paidNotPushed: number; failed: number;
        pushedGbpToday: number; pushedGbpWeek: number;
      };
      activity: { pageviewsLastMinute: number };
      /** Everything paid over the iFAST API, in GBP (other currencies at today's rates). */
      ifastApi?: { gbp: number; payments: number; byCurrency: { currency: string; payments: number; amount: number }[]; unconverted: string[] };
    }),

  getControlCentreTimeseries: (window: "24h" | "7d" | "30d") =>
    client.get("/admin/control-centre/timeseries", { params: { window } }).then((r) => r.data as {
      window: string;
      bucket: string;
      series: {
        filesUploaded: Array<{ bucket: string; value: number }>;
        pushedGbp: Array<{ bucket: string; value: number }>;
        newUsers: Array<{ bucket: string; value: number }>;
        invitesIssued: Array<{ bucket: string; value: number }>;
        invitesRegistered: Array<{ bucket: string; value: number }>;
        pageviews: Array<{ bucket: string; value: number }>;
      };
    }),

  getControlCentreRecentActivity: () =>
    client.get("/admin/control-centre/recent-activity").then((r) => r.data as {
      recentFiles: Array<{
        fileId: string; sfAccountId: string; filename: string; status: string;
        bankKind: "ifast" | "freemarket"; paymentCount: number | null;
        totalAmountGbp: number; uploadedAt: string | null;
      }>;
      recentSignups: Array<{
        userId: string; username: string; displayName: string | null;
        role: string; accountName: string | null;
        createdAt: string | null; lastLoginAt: string | null;
      }>;
      onlineUsers: Array<{
        userId: string; username: string; displayName: string | null;
        role: string; accountName: string | null; lastActiveAt: string | null;
      }>;
      topPagesLastHour: Array<{ path: string; hits: number }>;
    }),

  // Fire-and-forget pageview ping. Errors swallowed — must never break
  // navigation. Called from a route-change hook on every page.
  pingPageview: (path: string) =>
    client.post("/control-centre/pageview", { path }).catch(() => undefined),

  // ── Operations Tools: Sphere One Bulk Invoices ────────────
  // All endpoints staff-only. The paying client is hardcoded to
  // Sphere One in the backend; no accountId passed from the frontend.

  getSphereOneBulkBenefs: (accountId: string, search?: string) =>
    client
      .get("/ops/sphere-one-bulk/benefs", {
        params: { accountId, ...(search ? { search } : {}) },
      })
      .then((r) => r.data as {
        results: Array<{
          id: string; name: string; status: string;
          sortCode: string; accountNumber: string; bank: string;
          addressLine1: string; city: string; postCode: string;
        }>;
      }),

  postSphereOneBulkParse: (files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return client
      .post("/ops/sphere-one-bulk/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data as {
        invoices: Array<{
          filename: string;
          reference: string | null;
          totalGbp: number | null;
          issues: string[];
        }>;
        totals: { fileCount: number; validCount: number; totalGbp: number };
      });
  },

  postSphereOneBulkGenerate: (body: {
    accountId: string;
    beneficiaryId: string;
    paymentDate: string;
    invoices: Array<{ filename: string; reference: string | null; totalGbp: number | null }>;
  }) =>
    client.post("/ops/sphere-one-bulk/generate", body).then((r) => r.data as {
      batchId: string;
      csvFilename: string;
      validCount: number;
      totalGbp: number;
      beneficiary: { id: string; name: string };
    }),

  postSphereOneBulkPush: (batchId: string) =>
    client
      .post(`/ops/sphere-one-bulk/batches/${batchId}/push-to-sf`)
      .then((r) => r.data as {
        batchId: string;
        sfPaymentIds: string[];
        status: string;
      }),

  getSphereOneBulkBatches: (accountId: string) =>
    client
      .get("/ops/sphere-one-bulk/batches", { params: { accountId } })
      .then((r) => r.data as {
      batches: Array<{
        batchId: string;
        beneficiaryId: string;
        beneficiaryName: string;
        paymentDate: string | null;
        invoiceCount: number;
        totalAmountGbp: number;
        csvFilename: string;
        status: string;
        createdAt: string | null;
        pushedAt: string | null;
      }>;
    }),

  getSphereOneBulkBatchDetail: (batchId: string) =>
    client
      .get(`/ops/sphere-one-bulk/batches/${batchId}`)
      .then((r) => r.data as {
        batchId: string;
        beneficiaryId: string;
        beneficiaryName: string;
        paymentDate: string | null;
        invoices: Array<{
          filename: string;
          reference: string | null;
          totalGbp: number | null;
          issues: string[];
        }>;
        invoiceCount: number;
        totalAmountGbp: number;
        csvFilename: string;
        status: string;
        sfPaymentIds: string[];
        createdAt: string | null;
        pushedAt: string | null;
      }),

  deleteSphereOneBulkBatch: (batchId: string) =>
    client
      .delete(`/ops/sphere-one-bulk/batches/${batchId}`)
      .then((r) => r.data as { batchId: string; deleted: boolean }),

  // ── Compliance Tools: REP017 ──────────────
  // Staff-only, gated by mfx.compliance_tools.

  getRep017Periods: () =>
    client
      .get("/compliance/rep017/periods")
      .then((r) => r.data as { periods: Rep017Period[] }),

  getComplianceBanks: () =>
    client
      .get("/compliance/banks")
      .then((r) => r.data as { banks: Rep017Bank[] }),

  // Half a year of Completed payments is a large Salesforce pull, so this
  // runs well past the client's 15s default.
  postRep017: (body: {
    startDate: string;
    endDate: string;
    excludedAccountIds: string[];
    excludedBankIds: string[];
  }) =>
    client
      .post("/compliance/rep017", body, { timeout: 600_000 })
      .then((r) => r.data as Rep017Report),

  // Used by the Issue Reports add-item flow — uses the existing
  // /mass-payments/files endpoint but normalises the param surface.
  listMassPaymentFiles: (params: { accountId: string }) =>
    client
      .get("/mass-payments/files", { params: { accountId: params.accountId, scope: "client" } })
      .then((r) => r.data as {
        files: Array<{
          fileId: string;
          filename: string;
          paymentDate: string | null;
          isDeleted?: boolean;
        }>;
      }),

  // Single-file detail with payments_detail JSONB.
  getMassPaymentsFile: (fileId: string) =>
    client
      .get(`/mass-payments/files/${fileId}`)
      .then((r) => r.data as {
        fileId: string;
        sfAccountId: string;
        filename: string;
        paymentDate: string | null;
        status: string;
        bankKind: string;
        paymentCount: number | null;
        totalAmountGbp: number;
        excludedPaymentIndexes: number[];
        paymentsDetail: Array<Record<string, unknown>>;
      }),

  // ── Mass Payments → Issue Reports ─────────────────────
  getIssueReportCategories: () =>
    client
      .get("/mass-payments/issue-reports/categories")
      .then((r) => r.data as {
        categories: Array<{ value: string; label: string }>;
      }),

  listIssueReports: (accountId: string) =>
    client
      .get("/mass-payments/issue-reports", { params: { accountId } })
      .then((r) => r.data as {
        reports: Array<{
          id: string;
          sfAccountId: string;
          title: string;
          notes: string | null;
          status: string;
          itemCount: number;
          createdAt: string | null;
          updatedAt: string | null;
          sentAt: string | null;
        }>;
      }),

  createIssueReport: (body: { accountId: string; title: string; notes?: string }) =>
    client.post("/mass-payments/issue-reports", body).then((r) => r.data as { id: string }),

  getIssueReport: (reportId: string) =>
    client.get(`/mass-payments/issue-reports/${reportId}`).then((r) => r.data as {
      report: {
        id: string;
        sfAccountId: string;
        accountName: string;
        title: string;
        notes: string | null;
        status: string;
        createdAt: string | null;
        updatedAt: string | null;
        sentAt: string | null;
      };
      items: Array<{
        id: string;
        fileId: string | null;
        paymentIndex: number | null;
        benefName: string;
        sortCode: string;
        accountNumber: string;
        sourceFilename: string;
        amountGbp: number | null;
        issueCategory: string;
        issueCategoryLabel: string;
        issueNote: string | null;
        sortOrder: number;
      }>;
    }),

  patchIssueReport: (reportId: string, body: {
    title?: string;
    notes?: string;
    status?: string;
  }) =>
    client.patch(`/mass-payments/issue-reports/${reportId}`, body).then((r) => r.data),

  deleteIssueReport: (reportId: string) =>
    client.delete(`/mass-payments/issue-reports/${reportId}`).then((r) => r.data),

  addIssueReportItem: (reportId: string, body: {
    fileId?: string | null;
    paymentIndex?: number | null;
    benefName?: string;
    sortCode?: string;
    accountNumber?: string;
    sourceFilename?: string;
    amountGbp?: number | null;
    issueCategory: string;
    issueNote?: string | null;
  }) =>
    client.post(`/mass-payments/issue-reports/${reportId}/items`, body).then((r) => r.data as { id: string }),

  patchIssueReportItem: (itemId: string, body: {
    issueCategory?: string;
    issueNote?: string | null;
    benefName?: string;
    sortCode?: string;
    accountNumber?: string;
  }) =>
    client.patch(`/mass-payments/issue-reports/items/${itemId}`, body).then((r) => r.data),

  deleteIssueReportItem: (itemId: string) =>
    client.delete(`/mass-payments/issue-reports/items/${itemId}`).then((r) => r.data),

  getIssueReportText: (reportId: string) =>
    client
      .get(`/mass-payments/issue-reports/${reportId}/export/text`)
      .then((r) => r.data as { text: string }),

  // ── Notification engine ───────────────
  notifSearchAccounts: (q: string) =>
    client
      .get("/admin/notifications/accounts/search", { params: { q } })
      .then((r) => r.data as { results: NotifRecipient[] }),

  notifListGroups: () =>
    client
      .get("/admin/notifications/groups")
      .then((r) => r.data as { groups: NotifGroup[] }),

  notifCreateGroup: (body: { name: string; description?: string; account_ids: string[] }) =>
    client.post("/admin/notifications/groups", body).then((r) => r.data as NotifGroup),

  notifUpdateGroup: (
    id: string,
    body: { name?: string; description?: string; account_ids?: string[] },
  ) =>
    client
      .put(`/admin/notifications/groups/${id}`, body)
      .then((r) => r.data as { ok: boolean }),

  notifDeleteGroup: (id: string) =>
    client
      .delete(`/admin/notifications/groups/${id}`)
      .then((r) => r.data as { ok: boolean }),

  notifResolveAudience: (definition: NotifAudienceDefinition) =>
    client
      .post("/admin/notifications/resolve-audience", definition)
      .then((r) => r.data as { count: number; recipients: NotifRecipient[] }),

  notifSendCampaign: (body: {
    subject: string;
    body: string;
    channels: string[];
    audience_definition: NotifAudienceDefinition;
    email_header_url?: string | null;
  }) =>
    client
      .post("/admin/notifications/campaigns", body)
      .then(
        (r) =>
          r.data as {
            id: string;
            status: string;
            recipient_count: number;
            counts: Record<string, { sent?: number; failed?: number; skipped?: number }>;
          },
      ),

  notifListCampaigns: () =>
    client
      .get("/admin/notifications/campaigns")
      .then((r) => r.data as { campaigns: NotifCampaign[] }),

  // ── Email (Notifications composer) ───────────────
  emailStatus: () => client.get("/admin/email/status").then((r) => r.data as EmailStatus),

  emailListHeaders: () =>
    client
      .get("/admin/email/headers")
      .then((r) => r.data as { headers: EmailHeaderImage[]; default_url: string }),

  emailUploadHeader: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return client
      .post("/admin/email/headers", form, {
        timeout: 60_000,
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data as EmailHeaderImage);
  },

  emailDeleteHeader: (name: string) =>
    client
      .delete(`/admin/email/headers/${encodeURIComponent(name)}`)
      .then((r) => r.data as { ok: boolean }),

  emailPreview: (draft: EmailDraft) =>
    client.post("/admin/email/preview", draft).then((r) => r.data as { subject: string; html: string }),

  emailSendTest: (draft: EmailDraft) =>
    client
      .post("/admin/email/test", draft)
      .then(
        (r) =>
          r.data as {
            status: "sent" | "skipped" | "failed";
            detail: string;
            to: string;
            message_id?: string | null;
          },
      ),

  // Client-facing notification feed (bell + notifications page)
  getNotifications: (accountId: string) =>
    client.get("/notifications", { params: { accountId } }).then(
      (r) =>
        r.data as {
          count: number;
          unread: number;
          items: Array<{
            id: string;
            type: string;
            title: string;
            message: string;
            timestamp: string;
            read: boolean;
            source?: string;
          }>;
        },
    ),

  markNotificationRead: (id: string, accountId: string) =>
    client
      .post(`/notifications/${id}/read`, undefined, { params: { accountId } })
      .then((r) => r.data as { ok: boolean }),

  // ── Admin permissions matrix ───────────────────
  getPermissionsCatalogue: () =>
    client.get("/admin/permissions/catalogue").then((r) => r.data as {
      permissions: Array<{
        key: string;
        section: string;
        label: string;
        description: string;
      }>;
      mutexPairs: string[][];
      dependencies: Record<string, string>;
      roleDefaults: Record<string, string[]>;
    }),

  listStaffWithPermissions: () =>
    client.get("/admin/permissions/users").then((r) => r.data as {
      users: Array<{
        id: string;
        username: string;
        displayName: string;
        email: string | null;
        role: string;
        sfUserId: string | null;
        permissions: string[];
        isSuperAdmin: boolean;
        isRestricted: boolean;
        lastLoginAt: string | null;
        createdAt: string | null;
      }>;
    }),

  getStaffUserPermissions: (userId: string) =>
    client.get(`/admin/permissions/users/${userId}`).then((r) => r.data as {
      user: {
        id: string;
        username: string;
        displayName: string;
        email: string | null;
        role: string;
        sfUserId: string | null;
        permissions: string[];
        isSuperAdmin: boolean;
        isRestricted: boolean;
      };
      roleDefaults: string[];
      auditLog: Array<{
        id: string;
        permissionKey: string;
        action: string;
        reason: string | null;
        changedByUsername: string | null;
        changedByName: string | null;
        createdAt: string | null;
      }>;
    }),

  patchStaffUserPermissions: (userId: string, body: {
    permissions: string[];
    reason?: string;
  }) =>
    client.patch(`/admin/permissions/users/${userId}`, body).then((r) => r.data as {
      permissions: string[];
      granted: string[];
      revoked: string[];
    }),

  // Restricted account scope: limits a staff user to Test accounts plus
  // accounts they are the Salesforce Trader on.
  setStaffRestricted: (userId: string, enabled: boolean) =>
    client
      .post(`/admin/permissions/users/${userId}/restricted`, { enabled })
      .then((r) => r.data as { success: boolean; isRestricted: boolean }),

  // ── Impersonation (View As) ────────────────────
  listImpersonationEligibleUsers: () =>
    client.get("/impersonate/eligible-users").then((r) => r.data as {
      users: Array<{
        id: string;
        username: string;
        displayName: string;
        email: string | null;
        role: string;
        accountName: string | null;
        isSuperAdmin: boolean;
        lastLoginAt: string | null;
      }>;
    }),

  startImpersonation: (body: { targetUserId: string; reason?: string }) =>
    client.post("/impersonate/start", body).then((r) => r.data as {
      sessionId: string;
      startedAt: string;
      maxDurationSeconds: number;
      target: {
        id: string;
        username: string;
        displayName: string;
        email: string | null;
        role: string;
        accountId: string | null;
        accountName: string | null;
        sfUserId: string | null;
        isSuperAdmin: boolean;
        permissions: string[];
      };
    }),

  endImpersonation: (sessionId: string | null) =>
    client.post("/impersonate/end", { sessionId }).then((r) => r.data),

  /** Admin: revoke every session for another user, signing them out of
   *  the platform everywhere. Same admin gate as View As. */
  forceSignOutUser: (targetUserId: string) =>
    client.post("/admin/force-sign-out", { targetUserId }).then((r) => r.data as {
      success: boolean;
      revokedSessions: number;
      user: { id: string; username: string; displayName: string | null };
    }),

  // ── Leads ──────────────────────────────────────
  listLeads: (params?: { stage?: string; pmId?: string; source?: string }) =>
    client.get("/leads", { params }).then((r) => r.data as { leads: any[] }),

  /** Leads matching a search on any field: lead, contacts (name, email, phone…) or call notes. {leadId: where it matched}. */
  searchLeads: (q: string) =>
    client.get("/leads/search", { params: { q } }).then((r) => r.data as { matches: Record<string, string> }),

  getLead: (leadId: string) =>
    client.get(`/leads/${leadId}`).then((r) => r.data as {
      lead: any;
      callNoteDraft: { note: string; contactId: string | null; updatedAt: string | null } | null;
    }),

  getLeadFollowUps: () =>
    client.get("/leads/follow-ups").then((r) => r.data as { followUps: any[] }),

  // Self-sourced lead creation. 409 = a lead already exists on that
  // website domain; the message is deliberately generic and never names
  // the owning PM.
  createLead: (body: { fields: Record<string, any>; extraFields?: Record<string, any> }) =>
    client.post("/leads", body).then((r) => r.data as { lead: any }),

  getSelfSourcedLeads: () =>
    client.get("/leads/self-sourced").then((r) => r.data as SelfSourcedLeadsSummary),

  // ── MFX Hub (staff landing dashboard) ───────────────────────
  getHubLayout: () =>
    client.get("/hub/layout").then((r) => r.data as HubLayoutResponse),
  saveHubLayout: (layout: HubLayout) =>
    client.put("/hub/layout", { layout }).then((r) => r.data as HubLayoutResponse),
  resetHubLayout: () =>
    client.delete("/hub/layout").then((r) => r.data as HubLayoutResponse),
  // Staff's own left-hand menu (lib/navLayout.ts).
  getNavLayout: () =>
    client.get("/hub/nav-layout").then((r) => r.data as { layout: any }),
  saveNavLayout: (layout: unknown) =>
    client.put("/hub/nav-layout", { layout }).then((r) => r.data as { layout: any }),
  resetNavLayout: () =>
    client.delete("/hub/nav-layout").then((r) => r.data as { layout: any }),
  // ── Bank cut-off times ─────────────────────────────────────
  getCutoffConfig: () =>
    client.get("/cutoffs/config").then((r) => r.data as CutoffConfig),
  saveCutoffConfig: (body: {
    banks?: Array<{ bankId: string; bankName?: string; enabled: boolean }>;
    cutoffs?: Array<{ bankId: string; currency: string; label?: string; cutoff: string | null; alwaysOpen?: boolean }>;
    remove?: Array<{ bankId: string; currency: string; label: string }>;
  }) => client.put("/cutoffs/config", body).then((r) => r.data as { saved: boolean }),
  getCutoffsToday: () =>
    client.get("/cutoffs/today").then((r) => r.data as CutoffToday),

  getHubPms: () => client.get("/hub/pms").then((r) => r.data as { pms: Array<{ id: string; name: string }> }),
  // ── Trade booking ──────────────────────────────────────────
  searchBookingTrades: (q: string, unpaidOnly = false) =>
    client.get("/trade-booking/search", { params: { q, unpaidOnly: unpaidOnly ? 1 : 0 } }).then((r) => r.data as { trades: BookingTrade[] }),
  getBookingTrade: (id: string) =>
    client.get(`/trade-booking/${id}`).then((r) => r.data as { trade: BookingTrade }),
  bookTrade: (id: string, body: { brokerRate: number; lpReference?: string | null }) =>
    client.post(`/trade-booking/${id}/book`, body, { timeout: 60_000 }).then((r) => r.data as { trade: BookingTrade; written: { brokerRate: number; lpSellAmount: number; orientation: string; lpReference: string | null } }),
  getKpisTeamPulse: () =>
    client.get("/admin/kpis/team-pulse", { timeout: 180_000 }).then((r) => r.data as TeamPulse),
  getLeadActivity: (days = 7) =>
    client.get("/leads/activity", { params: { days } }).then((r) => r.data as LeadActivity),
  getMorningBriefing: (date?: string) =>
    client
      .get("/hub/briefing", { params: date ? { date } : undefined })
      .then((r) => r.data as { briefing: MorningBriefing | null }),
  generateMorningBriefing: (opts?: { date?: string; force?: boolean }) =>
    client
      .post("/hub/briefing/generate", null, { params: { date: opts?.date, force: opts?.force ? 1 : 0 } })
      .then((r) => r.data as { briefing: MorningBriefing | null }),
  getRiskOverview: (opts?: { bands?: number[]; positions?: boolean; pm?: string; client?: string }) =>
    client
      .get("/risk/overview", { params: {
        bands: opts?.bands?.length ? opts.bands.join(",") : undefined,
        positions: opts?.positions === false ? 0 : 1,
        pm: opts?.pm || undefined,
        client: opts?.client || undefined,
      } })
      .then((r) => r.data as RiskOverview),
  getRiskMargin: () => client.get("/risk/margin", { timeout: 120_000 }).then((r) => r.data as RiskMargin),
  getVelocityFiles: () =>
    client.get("/risk/velocity/files").then((r) => r.data as { files: VelocityFileRow[]; sftpConfigured: boolean }),
  uploadVelocityFile: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/risk/velocity/upload", fd, { timeout: 120_000, headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data as { duplicate: boolean; kind: string; rows: number | null; files: VelocityFileRow[] });
  },
  fetchVelocityFiles: () =>
    client.post("/risk/velocity/fetch", null, { timeout: 300_000 })
      .then((r) => r.data as { configured: boolean; message?: string; listed?: number; ingested?: { name: string; kind: string; rows: number }[];
        duplicates?: number; skipped?: { name: string; reason: string }[]; errors?: { name: string; error: string }[]; files: VelocityFileRow[] }),
  getHubWidgetData: <T = any>(widget: string, settings?: Record<string, string | number>) =>
    client
      .get(`/hub/widgets/${widget}`, { params: settings })
      .then((r) => r.data as T),

  deleteLeadContact: (leadId: string, contactId: string) =>
    client.delete(`/leads/${leadId}/contacts/${contactId}`).then((r) => r.data as {
      lead: any; deleted: boolean; promotedContactId: string | null;
    }),

  saveLeadCallNoteDraft: (leadId: string, note: string, contactId?: string) =>
    client.put(`/leads/${leadId}/call-note-draft`, { note, contactId }).then((r) => r.data as {
      draft: { note: string; contactId: string | null; updatedAt: string | null };
    }),

  getLeadHistory: (leadId: string) =>
    client.get(`/leads/${leadId}/history`).then((r) => r.data as { transitions: any[] }),

  updateLead: (leadId: string, body: { fields?: Record<string, any>; extraFields?: Record<string, any> }) =>
    client.patch(`/leads/${leadId}`, body).then((r) => r.data as { lead: any }),

  updateLeadContact: (leadId: string, contactId: string, body: { fields: Record<string, any> }) =>
    client.patch(`/leads/${leadId}/contacts/${contactId}`, body).then((r) => r.data as { lead: any }),

  promoteLeadPrimaryContact: (leadId: string, contactId: string) =>
    client.post(`/leads/${leadId}/promote-primary`, { contactId }).then((r) => r.data as { lead: any }),

  addLeadContact: (leadId: string, fields: Record<string, any>) =>
    client.post(`/leads/${leadId}/contacts`, { fields }).then((r) => r.data as { lead: any }),

  addLeadCallNote: (leadId: string, note: string, contactId?: string) =>
    client.post(`/leads/${leadId}/call-notes`, { note, contactId }).then((r) => r.data as { lead: any }),

  previewLeadImport: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/leads/import/preview", form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data as {
      headers: string[];
      firstRow: Record<string, string>;
      firstNonblankPerColumn: Record<string, string>;
      rowCount: number;
      guessedMapping: Record<string, string | null>;
    });
  },

  performLeadImport: (file: File, columnMapping: Record<string, string | null>) => {
    const form = new FormData();
    form.append("file", file);
    form.append("columnMapping", JSON.stringify(columnMapping));
    return client.post("/leads/import", form, {
      timeout: 120_000,  // large CSV / XLSX could be slow
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data as {
      importId: string;
      rowsTotal: number;
      rowsImported: number;
      rowsSkipped: number;
      skippedAccountNames: string[];
    });
  },

  assignLeads: (body: {
    distributeEvenly: boolean;
    totalToDistribute?: number;
    includedPmIds?: string[];
    perPmCounts?: Record<string, number>;
    source?: string;
  }) =>
    client.post("/leads/assign", body).then((r) => r.data as {
      assignedCount: number;
      requestedCount: number;
      perPmAssigned: Record<string, number>;
      shortfall: number;
    }),

  getLeadSources: () =>
    client.get("/leads/sources").then((r) => r.data as {
      sources: Array<{ sourceFile: string; total: number; unassigned: number; lastImported: string | null }>;
    }),

  withdrawLeads: (body:
    | { mode: "soft_all"; pmId: string }
    | { mode: "soft_custom"; pmId: string; limit: number }
    | { mode: "specific"; leadIds: string[] }
    | { mode: "departing_pm"; pmId: string }
  ) =>
    client.post("/leads/withdraw", body).then((r) => r.data as
      | {  // soft_all / soft_custom / specific
          withdrawnCount: number;
          lockedCount: number;
          withdrawnIds: string[];
          lockedIds: string[];
        }
      | {  // departing_pm
          softWithdrawn: number;
          assignedWithdrawn: number;
          workingWithdrawn: number;
          totalWithdrawn: number;
        }
    ),

  getLeadsOverview: () =>
    client.get("/leads/overview").then((r) => r.data as {
      overview: {
        byDisplayStage: Record<string, { leadCount: number; totalEstRev: number }>;
        totals: { leadCount: number; totalEstRev: number };
      };
      perPm: Array<any>;
    }),

  getLeadsPmSummary: () =>
    client.get("/leads/pm-summary").then((r) => r.data as { perPm: any[] }),

  // ── Bank Payments ────────────────────────────────────────────
  getBankPaymentsOverview: () =>
    client.get("/bank-payments/overview").then((r) => r.data as BpOverview),

  runIfastRecon: (text: string) =>
    client.post("/bank-payments/ifast-recon", { text }, { timeout: 180_000 })
      .then((r) => r.data as IfastReconResult),

  // ── Bank Payments → iFAST statements ─────────────────────────
  getIfastStatementAccounts: (refresh = false) =>
    client.get("/bank-payments/ifast-statements/accounts", { params: { refresh }, timeout: 60_000 })
      .then((r) => r.data as { accounts: IfastStatementAccount[]; earliest: string; today: string }),
  getIfastStatementExports: () =>
    client.get("/bank-payments/ifast-statements/exports")
      .then((r) => r.data as { exports: IfastStatementExport[] }),
  startIfastStatementExport: (body: { all: boolean; ibans?: string[]; dateFrom: string; dateTo: string }) =>
    client.post("/bank-payments/ifast-statements/exports", body, { timeout: 60_000 })
      .then((r) => r.data as { export: IfastStatementExport }),
  cancelIfastStatementExport: (id: string) =>
    client.post(`/bank-payments/ifast-statements/exports/${id}/cancel`)
      .then((r) => r.data as { export: IfastStatementExport }),
  downloadIfastStatementExport: async (exp: IfastStatementExport) => {
    const resp = await client.get(`/bank-payments/ifast-statements/exports/${exp.id}/download`, {
      responseType: "blob", timeout: 120_000,
    });
    const url = URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = exp.filename || "ifast-statement";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  getBpInvoiceSuggestionReport: () =>
    client.get("/dev/invoice-suggestions", { timeout: 60_000 }).then((r) => r.data as BpSuggestionReport),
  bpIfastClientBreakdown: (bankAccountId: string) =>
    client.get("/bank-payments/ifast/client-breakdown", { params: { bankAccountId }, timeout: 120_000 })
      .then((r) => r.data as { bankAccountId: string; total: number;
        clients: { accountId: string; accountName: string; balance: number; transactions: number; share: number | null }[] }),
  bpIfastStatement: (iban: string, currency?: string) =>
    client.get("/bank-payments/ifast/statement", { params: { iban, currency, limit: 50 } })
      .then((r) => r.data as IfastStatement),

  // A full pass across both Freemarket environments runs well past the
  // 15s default, so this call gets its own timeout.
  runBankPaymentsScan: () =>
    client.post("/bank-payments/scan", undefined, { timeout: 600_000 })
      .then((r) => r.data as Record<string, any>),

  getBankPaymentsQueue: (status: string) =>
    client.get("/bank-payments/queue", { params: { status } })
      .then((r) => r.data as { items: BpQueueItem[]; matchedInSf: number }),

  getBankPaymentItem: (id: string) =>
    client.get(`/bank-payments/queue/${id}`)
      .then((r) => r.data as {
        item: BpQueueItem;
        problems: string[];
        suggestedCreditInstitutionName: string;
      }),

  createBpCreditInstitution: (id: string, name: string) =>
    client.post(`/bank-payments/queue/${id}/credit-institution`, { name })
      .then((r) => r.data as {
        item: BpQueueItem;
        creditInstitution: { id: string; name: string; created: boolean };
        problems: string[];
      }),

  updateBankPaymentItem: (id: string, prep: Partial<BpPrep>) =>
    client.patch(`/bank-payments/queue/${id}`, { prep })
      .then((r) => r.data as { item: BpQueueItem; problems: string[] }),

  refreshBankPaymentRate: (id: string) =>
    client.post(`/bank-payments/queue/${id}/refresh-rate`)
      .then((r) => r.data as { item: BpQueueItem; problems: string[] }),

  ignoreBankPaymentItem: (id: string, note?: string) =>
    client.post(`/bank-payments/queue/${id}/ignore`, { note })
      .then((r) => r.data as { item: BpQueueItem }),

  sendBankPaymentToCompliance: (id: string, note?: string) =>
    client.post(`/bank-payments/queue/${id}/compliance`, { note })
      .then((r) => r.data as { item: BpQueueItem }),

  clearBankPaymentCompliance: (id: string, note?: string) =>
    client.post(`/bank-payments/queue/${id}/compliance/clear`, { note })
      .then((r) => r.data as { item: BpQueueItem; problems: string[] }),

  restoreBankPaymentItem: (id: string) =>
    client.post(`/bank-payments/queue/${id}/restore`)
      .then((r) => r.data as { item: BpQueueItem }),

  pushBankPaymentItem: (id: string) =>
    client.post(`/bank-payments/queue/${id}/push`).then((r) => r.data as {
      item: BpQueueItem; bankPaymentId: string;
      invoiceId: string | null; transactionId: string | null;
      balance: {
        currency: string; before: number; amount: number; after: number;
        asOf: string | null; nostroAccountName: string; clientName: string;
      } | null;
    }),

  getBalanceSettings: () =>
    client.get("/bank-payments/balance-settings").then((r) => r.data as { accounts: ExcludedAccount[] }),
  searchBalanceSettingsAccounts: (q: string) =>
    client.get("/bank-payments/balance-settings/accounts", { params: { q } })
      .then((r) => r.data as { results: { id: string; name: string }[] }),
  addBalanceExclusion: (accountId: string, accountName: string, reason: string) =>
    client.post("/bank-payments/balance-settings", { accountId, accountName, reason })
      .then((r) => r.data as { accounts: ExcludedAccount[] }),
  removeBalanceExclusion: (accountId: string) =>
    client.delete(`/bank-payments/balance-settings/${accountId}`).then((r) => r.data as { accounts: ExcludedAccount[] }),

  getBpExclusions: () =>
    client.get("/bank-payments/exclusions")
      .then((r) => r.data as { exclusions: BpExclusion[] }),

  addBpExclusion: (accountId: string, accountName: string, reason: string, banks?: string[]) =>
    client.post("/bank-payments/exclusions", { accountId, accountName, reason, banks })
      .then((r) => r.data as { exclusions: BpExclusion[] }),

  setBpExclusionBanks: (accountId: string, banks: string[]) =>
    client.put(`/bank-payments/exclusions/${accountId}/banks`, { banks })
      .then((r) => r.data as { exclusions: BpExclusion[] }),

  removeBpExclusion: (accountId: string) =>
    client.delete(`/bank-payments/exclusions/${accountId}`)
      .then((r) => r.data as { exclusions: BpExclusion[] }),

  getBpCounterparties: (limit = 500) =>
    client.get("/bank-payments/counterparties", { params: { limit } })
      .then((r) => r.data as { entries: BpCounterparty[]; stats: BpMapStats }),

  withdrawBankPaymentItem: (id: string) =>
    client.post(`/bank-payments/queue/${id}/withdraw`).then((r) => r.data as {
      item: BpQueueItem; steps: string[]; problems: string[];
    }),

  // ── Dev Zone (super admin) ───────────────────────────────────
  getDevLabelCandidates: (accountId: string, limit = 100) =>
    client.get("/dev/label-candidates", { params: { accountId, limit }, timeout: 120_000 })
      .then((r) => r.data as {
        candidates: {
          transactionId: string; env: string; amount: number | null;
          currency: string | null; valueDate: string | null;
          payer: string | null; identifier: string | null; bank: string | null;
          fmReference: string; narrative: string[];
          guess: { value: string; source: string; confidence: string; reason: string };
        }[];
        count: number;
        errors: string[];
      }),

  saveDevLabel: (body: {
    transactionId: string; identifier: string; narrative: string[];
    fmReference: string; invoiceNumber: string; noReference: boolean;
    suggested: string;
  }) => client.post("/dev/label", body).then((r) => r.data as { saved: boolean }),

  getDevLabelStats: () =>
    client.get("/dev/label-stats").then((r) => r.data as {
      training: {
        labelled: number; withReference: number; noReference: number;
        rulesCorrect: number; rulesAccuracy: number | null;
        rulesMissedEntirely: number;
      };
      live: Record<string, any>;
      sources?: {
        bySource: Record<string, { count: number; noReference: number }>;
        total: number;
        backfilled: number;
      };
    }),

  // ── Dev Zone: historic training (label back-fill) ─────────────
  getDevHistoricBankAccounts: () =>
    client.get("/dev/historic/bank-accounts").then((r) => r.data as {
      bankAccounts: HistoricBankAccount[];
    }),

  previewDevHistoric: (params: {
    bankAccountId: string; months: number; toleranceDays: number; maxPages: number;
  }) =>
    // Walks the Freemarket deposit history a page at a time behind a SOQL
    // pull, so it runs for minutes rather than the default 15 seconds.
    client.get("/dev/historic/preview", { params, timeout: 600_000 }).then(
      (r) => r.data as HistoricPreview),

  importDevHistoric: (body: {
    bankAccountId: string; months: number; toleranceDays: number; maxPages: number;
  }) =>
    client.post("/dev/historic/import", body, { timeout: 600_000 }).then((r) => r.data as {
      funnel: HistoricFunnel;
      cacheHit: boolean;
      pairs: number;
      usable: number;
      written: number;
      alreadyThere: number;
      skipped: number;
      failed: number;
      firstError: string;
      stats: { bySource: Record<string, { count: number }>; total: number; backfilled: number };
    }),

  getDevLabelAnalysis: () =>
    client.get("/dev/label-analysis").then((r) => r.data as {
      total: number;
      outcome: { correct: number; wrong: number; missed: number; noReference: number };
      placement: { wholeLineValue: number; fragment: number; notFound: number };
      positionInLine: { start: number; middle: number; end: number };
      lineIndex: { value: string; count: number }[];
      lineLabels: { value: string; count: number }[];
      precedingWords: { value: string; count: number }[];
      followingWords: { value: string; count: number }[];
      shapes: Record<string, number>;
      length: { min: number | null; median: number | null; max: number | null };
      wrongExamples: { actual: string; suggested: string; wholeLineSuggested: string }[];
    }),

  trainDevModel: () =>
    client.post("/dev/model/train", undefined, { timeout: 300_000 })
      .then((r) => r.data as {
        trained: boolean; promoted?: boolean; reason?: string;
        metrics: {
          accuracy: number; testSize: number; trainSize: number;
          incumbentAccuracy: number | null; droppedLabels: number;
          candidatesPerExample: number; maxTokens?: number;
          // Payer-held-out evaluation. `accuracy` counts every label equally;
          // `macroAccuracy` counts every payer equally.
          macroAccuracy?: number | null;
          split?: {
            version: string;
            trainLabels: number; trainPayers: number;
            valLabels: number; valPayers: number;
            testLabels: number; testPayers: number;
            largestTestPayerShare: number;
          };
          testPayers?: { payer: string; labels: number; accuracy: number }[];
          sweep?: {
            // Validation accuracy — what the cap is chosen on.
            maxTokens: number; accuracy: number | null; valSize?: number;
            usableLabels: number;
            droppedLabels: number; candidatesPerExample: number;
          }[];
        };
        weights?: Record<string, number>;
      }),

  getDevLabelFixes: () =>
    client.get("/dev/label-fixes").then((r) => r.data as {
      proposals: { id: number; current: string; proposed: string; line: string;
                   identifier: string | null }[];
      review: LabelReviewRow[];
      filler: LabelFillerReport;
    }),

  // `edits` carries values typed on the screen; they win over the proposed
  // trim for the same label. `noReference` marks the narrative as carrying
  // nothing that belongs in the invoice field.
  applyDevLabelFixes: (
    ids: number[],
    edits: { id: number; value?: string; noReference?: boolean }[] = [],
  ) =>
    client.post("/dev/label-fixes/apply", { ids, edits }).then((r) => r.data as {
      changed: number;
      proposals: { id: number; current: string; proposed: string; line: string;
                   identifier: string | null }[];
      review: LabelReviewRow[];
      filler: LabelFillerReport;
    }),

  // Training data for the local extraction model. Built from every label,
  // so allow it longer than the default timeout.
  exportDevTrainingData: () =>
    client.get("/dev/model/export", { timeout: 120_000 }).then((r) => r.data as {
      format: string;
      splitVersion: string;
      exportedAt: string;
      labels: unknown[];
    }),

  // ── Reference extractor ──
  getDevExtractorStatus: () =>
    client.get("/dev/extractor/status", { timeout: 60_000 }).then(
      (r) => r.data as ExtractorStatus),

  createDevExtractorUploadUrl: () =>
    client.post("/dev/extractor/upload-url").then(
      (r) => r.data as { uploadUrl: string; path: string }),

  // Downloads, unpacks and test-runs the package before switching to it,
  // so it runs for a while.
  activateDevExtractor: (path: string) =>
    client.post("/dev/extractor/activate", { path }, { timeout: 600_000 }).then(
      (r) => r.data as { modelId: number; sizeBytes: number; status: ExtractorStatus }),

  getDevModelErrors: () =>
    client.get("/dev/model/errors").then((r) => r.data as {
      ready: boolean; reason?: string;
      testSize?: number; wrongCount?: number; accuracy?: number | null;
      patterns?: { kind: string; count: number }[];
      examples?: { actual: string; predicted: string; line: string; kind: string }[];
    }),

  getDevModelStatus: () =>
    client.get("/dev/model/status").then((r) => r.data as {
      active: {
        metrics: Record<string, any>; labelCount: number; trainedAt: string | null;
        maxTokens?: number;
      } | null;
      liveAccuracy: number | null;
      history: {
        id: number; metrics: Record<string, any>; labelCount: number;
        active: boolean; trainedAt: string | null; note: string | null;
      }[];
      features: string[];
    }),

  getBankPaymentsAudit: (limit = 200, scope: "pushes" | "user" | "all" = "pushes") =>
    client.get("/bank-payments/audit", { params: { limit, scope } })
      .then((r) => r.data as { entries: BpAuditEntry[] }),

  searchBpCreditInstitutions: (q: string) =>
    client.get("/bank-payments/lookups/credit-institutions", { params: { q } })
      .then((r) => r.data as { results: BpLookupResult[] }),

  searchBpAccounts: (q: string) =>
    client.get("/bank-payments/lookups/accounts", { params: { q } })
      .then((r) => r.data as { results: BpLookupResult[] }),

  searchBpBankAccounts: (q: string) =>
    client.get("/bank-payments/lookups/bank-accounts", { params: { q } })
      .then((r) => r.data as { results: BpLookupResult[] }),

  rederiveBankPaymentItem: (id: string) =>
    client.post(`/bank-payments/queue/${id}/rederive`)
      .then((r) => r.data as { item: BpQueueItem; problems: string[] }),

  searchBpThirdParties: (accountId: string, q: string) =>
    client.get("/bank-payments/lookups/third-parties", { params: { accountId, q } })
      .then((r) => r.data as { results: BpLookupResult[] }),

  getLeadKpis: (period: LeadKpiPeriod) =>
    client.get("/leads/kpis", { params: { period } }).then((r) => r.data as LeadKpis),

  pushLeadToSf: (leadId: string, primaryContactId?: string) =>
    client.post(`/leads/${leadId}/push-to-sf`, { primaryContactId }).then((r) => r.data as {
      sfLeadId: string;
      stage: string;
    }),

  /** Push platform edits up to a lead that's already in Salesforce.
   *  SF keeps its own Status; only the enriched fields are sent. */
  pushLeadUpdatesToSf: (leadId: string, primaryContactId?: string) =>
    client.post(`/leads/${leadId}/push-updates`, { primaryContactId }).then((r) => r.data as {
      sfLeadId: string;
      fieldsUpdated: number;
      sfPendingChanges: boolean;
    }),

  setLeadOutcome: (leadId: string, outcome: "hard_no" | "freeze", reason: string) =>
    client.post(`/leads/${leadId}/outcome`, { outcome, reason }).then((r) => r.data as {
      lead: any;
    }),

  reactivateLead: (leadId: string) =>
    client.post(`/leads/${leadId}/reactivate`).then((r) => r.data as { lead: any }),

  pollLeadsSf: () =>
    client.post("/leads/poll-sf").then((r) => r.data as {
      polledCount: number;
      updatedCount: number;
      polledAt: string;
    }),

  // ── Leads: batch management (admin only) ──────────────────────
  // A "batch" is one lead_imports row — one file upload. Keyed on
  // importId, not filename, so re-uploading the same filename stays
  // separately addressable.

  listLeadBatches: () =>
    client.get("/leads/batches").then((r) => r.data as { batches: LeadBatch[] }),

  getLeadBatch: (importId: string, includeDeleted = false) =>
    client
      .get(`/leads/batches/${importId}`, { params: { includeDeleted } })
      .then((r) => r.data as {
        batch: LeadBatch;
        leads: LeadBatchLead[];
        deletions: LeadBatchDeletion[];
      }),

  previewLeadBatchDelete: (
    importId: string,
    body: { excludeStages?: string[]; includePushedToSf?: boolean } = {},
  ) =>
    client
      .post(`/leads/batches/${importId}/delete/preview`, body)
      .then((r) => r.data as LeadBatchDeletePreview),

  deleteLeadBatch: (
    importId: string,
    body: { excludeStages?: string[]; includePushedToSf?: boolean },
  ) =>
    client
      .post(`/leads/batches/${importId}/delete`, { ...body, confirm: "DELETE" })
      .then((r) => r.data as {
        deletionId: string;
        importId: string;
        filename: string;
        deletedCount: number;
        byStage: Record<string, number>;
        excludedStages: string[];
        includedPushedToSf: boolean;
      }),

  restoreLeadBatchDeletion: (deletionId: string) =>
    client
      .post(`/leads/batches/deletions/${deletionId}/restore`)
      .then((r) => r.data as {
        deletionId: string;
        importId: string;
        restoredCount: number;
        skippedAlreadyActive: number;
        skippedNameCollision: string[];
      }),

  listLeadBatchDeletions: (importId?: string) =>
    client
      .get("/leads/batches/deletions", { params: importId ? { importId } : {} })
      .then((r) => r.data as { deletions: LeadBatchDeletion[] }),

  // Single-lead soft delete. `force` is required for leads already
  // pushed to SF — the backend returns 409 without it.
  deleteLead: (leadId: string, force = false) =>
    client
      .delete(`/leads/${leadId}`, { params: { force } })
      .then((r) => r.data as {
        leadId: string;
        accountName: string | null;
        stage: string;
        sfLeadId: string | null;
      }),

  restoreLead: (leadId: string) =>
    client
      .post(`/leads/${leadId}/restore`)
      .then((r) => r.data as { leadId: string; accountName: string | null }),

  // ── Bank Statement Reconciliation ────────────────────────────
  // Chunked upload: the file is sent in small 2MB pieces, each its own
  // tiny request, then reassembled server-side. This keeps every request
  // far below any body-size / connection limit, so large (32k+ row)
  // statements upload reliably. onProgress(0-100) drives a progress bar.
  uploadReconStatement: async (
    sfAccountId: string,
    file: File,
    onProgress?: (pct: number) => void,
    scope?: { scope: "all" | "today" | "week" | "custom"; dateFrom?: string; dateTo?: string },
  ): Promise<{ jobId: string; status: string }> => {
    const CHUNK = 2 * 1024 * 1024; // 2MB
    const total = Math.max(1, Math.ceil(file.size / CHUNK));

    const initParams = new URLSearchParams({
      sfAccountId,
      filename: file.name,
      scope: scope?.scope ?? "all",
    });
    if (scope?.scope === "custom") {
      if (scope.dateFrom) initParams.set("dateFrom", scope.dateFrom);
      if (scope.dateTo) initParams.set("dateTo", scope.dateTo);
    }
    const initRes = await client.post(`/recon/upload/init?${initParams.toString()}`);
    const jobId: string = initRes.data.jobId;

    for (let i = 0; i < total; i++) {
      const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
      // Retry a chunk up to 3 times before giving up.
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await client.post(
            `/recon/upload/chunk?jobId=${encodeURIComponent(jobId)}&index=${i}`,
            blob,
            { headers: { "Content-Type": "application/octet-stream" }, timeout: 120_000 },
          );
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      if (lastErr) throw lastErr;
      if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
    }

    const finRes = await client.post(
      `/recon/upload/finalize?jobId=${encodeURIComponent(jobId)}&chunks=${total}`,
    );
    return finRes.data as { jobId: string; status: string };
  },

  listReconJobs: (params: { sfAccountId?: string; limit?: number } = {}) =>
    client
      .get("/recon/jobs", {
        params: { sfAccountId: params.sfAccountId, limit: params.limit },
      })
      .then((r) => r.data as { jobs: ReconJob[] }),

  getReconJob: (jobId: string) =>
    client.get(`/recon/jobs/${jobId}`).then((r) => r.data as { job: ReconJob }),

  getReconResults: (
    jobId: string,
    params: {
      bucket?: "matched" | "ref_matched" | "count_matched" | "returned_pair" | "retry" | "unmatched" | "ignored";
      direction?: "in" | "out" | "ignored";
      search?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    client
      .get(`/recon/jobs/${jobId}/results`, { params })
      .then((r) => r.data as {
        results: ReconResult[];
        total: number;
        limit: number;
        offset: number;
      }),

  downloadReconReport: async (jobId: string, filename: string) => {
    const resp = await client.get(`/recon/jobs/${jobId}/report.csv`, {
      responseType: "blob",
    });
    const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  cancelReconJob: (jobId: string) =>
    client.post(`/recon/jobs/${jobId}/cancel`).then((r) => r.data as { cancelled: boolean }),

} as const;

// ── Recon types ────────────────────────────────────────────────
export interface ReconJob {
  id: string;
  sfAccountId: string;
  statementAccountName: string | null;
  statementIban: string | null;
  statementAccountNo: string | null;
  statementCurrency: string | null;
  statementTxCount: number | null;
  bankAccountSfId: string | null;
  uploadFilename: string | null;
  status: "parsing" | "fetching_sf" | "matching" | "complete" | "failed" | "cancelled";
  statusMessage: string | null;
  progressPct: number;
  totalRows: number | null;
  rowsIn: number | null;
  rowsOut: number | null;
  rowsIgnored: number | null;
  matchedCount: number | null;
  refMatchedCount: number | null;
  countMatchedCount: number | null;
  unmatchedCount: number | null;
  returnedPairCount: number | null;
  retryCount: number | null;
  errorDetail: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdByUsername: string | null;
  scope: "all" | "today" | "week" | "custom";
  scopeFrom: string | null;
  scopeTo: string | null;
  /** Where the statement came from: uploaded by hand, or fetched from iFAST (API mode). */
  statementSource?: "upload" | "ifast";
}

export interface ReconResult {
  id: string;
  bankTxId: string | null;
  bookingDate: string | null;
  valueDate: string | null;
  direction: "in" | "out" | "ignored";
  amount: number | null;
  currency: string | null;
  bankTxCode: string | null;
  counterpartyName: string | null;
  counterpartyAccount: string | null;
  counterpartySort: string | null;
  counterpartyReference: string | null;
  bucket: "matched" | "ref_matched" | "count_matched" | "returned_pair" | "retry" | "unmatched" | "ignored";
  sfRecordId: string | null;
  sfRecordType: string | null;
  sfRecordName: string | null;
  sfRecordReference: string | null;
  sfRecordAmount: number | null;
  sfRecordStatus: string | null;
  sfBeneficiaryName: string | null;
  sfBeneficiaryIban: string | null;
  sfBeneficiarySort: string | null;
  pairedWithResultId: string | null;
  notes: string | null;
}


/** Where a Bank Payments item or balance came from: "Freemarket UK", "iFAST"… */
export function bankSourceLabel(env: string | null | undefined): string {
  const e = (env || "").toLowerCase();
  if (e === "ifast") return "iFAST";
  if (e === "crown") return "Crown";
  return `Freemarket ${e.toUpperCase()}`.trim();
}


export interface IfastStatementLine {
  transactionId: string; paymentId: string; bookedAt: string; valueDate: string;
  direction: "in" | "out"; amount: number; currency: string; scheme: string; status: string;
  counterparty: string; counterpartyAccount: string; reference: string; details: string;
  relatedPaymentId?: string; requestId?: string; bankTransactionCode?: string; crdrIndicator?: string;
  vrn?: string; viban?: string; accountIban?: string;
}
export interface IfastStatement { iban: string; transactions: IfastStatementLine[]; searched: string; count: number }


/** When the KPI data was last pulled from Salesforce, and the next scheduled pull. */
export interface KpisStatus { loadedAt: string | null; nextScheduled: string; schedule: string[]; timezone: string }
